import { isBefore, parse } from 'date-fns';
import { convertDailyRestrictionToArray, convertDailyRestrictionToJson } from './conversionFunctions';

export function normalizeCriteriaConfiguration(criteria) {
  if (!criteria) {
    return null;
  }

  let parsedConfiguration = criteria.criteriaConfiguration ?? criteria;

  if (typeof parsedConfiguration === 'string') {
    try {
      parsedConfiguration = JSON.parse(parsedConfiguration);
    } catch (error) {
      console.warn('Failed to parse criteria configuration JSON:', error);
      return criteria;
    }
  } else if (parsedConfiguration && typeof parsedConfiguration === 'object') {
    parsedConfiguration = { ...parsedConfiguration };
  }

  if (!parsedConfiguration || typeof parsedConfiguration !== 'object') {
    return parsedConfiguration;
  }

  if (parsedConfiguration.affSubIds) {
    const { includeExclude, affiliate, subIds } = parsedConfiguration.affSubIds;
    const normalizedSubIds = Array.isArray(subIds)
      ? subIds.map((id) => (id === null || id === undefined ? '' : `${id}`.trim())).filter((id) => id.length > 0)
      : typeof subIds === 'string'
      ? subIds
          .split('\n')
          .map((id) => id.trim())
          .filter((id) => id.length > 0)
      : [];

    parsedConfiguration.affSubIds = {
      includeExclude,
      affiliate: affiliate || '',
      subIds: normalizedSubIds,
    };
  }

  if (typeof parsedConfiguration.zipsValues === 'string') {
    parsedConfiguration.zipsValues = parsedConfiguration.zipsValues.trim()
      ? parsedConfiguration.zipsValues
          .split(/[\s,]+/)
          .map((zip) => zip.trim())
          .filter((zip) => zip.length > 0)
      : [];
  } else if (parsedConfiguration.zipsValues === null) {
    parsedConfiguration.zipsValues = [];
  }

  // Convert empty string anuraScore to null to avoid enum deserialization errors
  if (parsedConfiguration.anuraScore === '') {
    parsedConfiguration.anuraScore = null;
  }

  // Convert jobKeywords string to array — backend expects List<String>
  if (parsedConfiguration.jobKeyword && typeof parsedConfiguration.jobKeyword.jobKeywords === 'string') {
    const raw = parsedConfiguration.jobKeyword.jobKeywords;
    parsedConfiguration.jobKeyword = {
      ...parsedConfiguration.jobKeyword,
      jobKeywords: raw.trim()
        ? raw
            .split(/[,\r?\n]+/)
            .map((t) => t.trim())
            .filter((t) => t.length > 0)
        : [],
    };
  }

  return parsedConfiguration;
}

/**
 * Merges the new criteria panel's normalized output with the authoritative
 * cap fields produced by handleCriteriaSubmit (via offerCriteria/values).
 */
export function buildCriteriaPayload(values, offerCriteria) {
  const normalized = normalizeCriteriaConfiguration(values.criteria);
  const { capInterval, capAmount, capConfiguration, capGroup } = offerCriteria;
  const internalPrePingConfiguration = normalized?.internalPrePings ?? offerCriteria.internalPrePingConfiguration;
  const domainAffiliateConfiguration = normalized?.domainAffiliates ?? offerCriteria.domainAffiliateConfiguration;
  const previousRegistrationConfiguration =
    normalized?.previousRegistrations ?? offerCriteria.previousRegistrationConfiguration;
  const userAttributeRules = normalized?.userAttributes ?? offerCriteria.userAttributeRules;
  const prePingMethod = normalized?.prePingMethod ?? offerCriteria.prePingMethod ?? offerCriteria.prePingType ?? null;
  const prePingType = prePingMethod;
  return {
    criteriaConfiguration: {
      ...normalized,
      capInterval,
      capAmount,
      capConfiguration,
      capGroup,
      prePingMethod,
      prePingType,
    },
    mergedOfferCriteria: {
      ...offerCriteria,
      ...normalized,
      capInterval,
      capAmount,
      capConfiguration,
      capGroup,
      internalPrePingConfiguration,
      domainAffiliateConfiguration,
      previousRegistrationConfiguration,
      userAttributeRules,
      userAttributes: null,
      prePingMethod,
      prePingType,
    },
    capInterval,
    capAmount,
  };
}

// Safely parse a cap group's additionalData JSON. Returns {} on missing/invalid
// so one malformed record can't break the whole cap-group list.
export function parseCapGroupAdditionalData(additionalData) {
  if (!additionalData) return {};
  try {
    return JSON.parse(additionalData);
  } catch {
    return {};
  }
}

// Resolve a cap group from the fetched list (the DB source of truth) by id.
// The criteria_configuration JSON now stores only the cap group id, so name and
// capAmount must be read from here, never from an embedded snapshot.
// Returns { id, name, capAmount } or null when the id is absent/not found.
export function resolveCapGroup(capGroups, id) {
  if (id == null) return null;
  const found = (capGroups || []).find((g) => Number(g.id) === Number(id));
  if (!found) return null;
  return { id: found.id, name: found.name, capAmount: found.additionalData?.capAmount ?? null };
}

export function handleCriteriaAffNames(affNames) {
  return affNames
    ? affNames.map((affName) => {
        if (typeof affName === 'object') {
          return affName.name;
        } else {
          if (affName === '' || affName === '[None]') return { name: '[None]', id: null };
          if (affName === '[Null]') return affName;
          return affName;
        }
      })
    : [];
}

export function handleCriteriaDomains(domains) {
  return domains
    ? domains.map((domain) => {
        if (typeof domain === 'object' && domain.name) {
          return domain.name;
        } else {
          return domain;
        }
      })
    : [];
}

export const handleCriteriaDomainAffiliateNames = (entries) => {
  if (!entries || typeof entries !== 'object') return null;
  if (entries.hasOwnProperty('domainAffiliateEntries')) {
    const domainAffiliateEntries = entries.domainAffiliateEntries.map((entry) => {
      const { affiliates, domains, ...rest } = entry;

      const processedAffiliates = affiliates?.map((affiliate) =>
        typeof affiliate === 'object' && affiliate.name ? affiliate.name : affiliate
      );

      const processedDomains = domains?.map((domain) =>
        typeof domain === 'object' && domain.name ? domain.name : domain
      );

      return {
        ...rest,
        ...(processedAffiliates ? { affiliates: processedAffiliates } : {}),
        ...(processedDomains ? { domains: processedDomains } : {}),
      };
    });

    return {
      ...entries,
      domainAffiliateEntries: domainAffiliateEntries,
    };
  } else {
    return entries;
  }
};

function processInboundJobKeyword(jobKeywordData) {
  const { includeExclude = 'None', jobKeywords: kwInput = [] } =
    jobKeywordData && typeof jobKeywordData === 'object' ? jobKeywordData : {};

  let text;
  if (Array.isArray(kwInput)) {
    // join real newlines
    text = kwInput.join('\n');
  } else if (typeof kwInput === 'string') {
    // replace literal “\n” sequences with actual newlines
    text = kwInput.replace(/\\n/g, '\n');
  } else {
    text = '';
  }

  return { includeExclude, jobKeywords: text };
}

function processOutboundJobKeyword(jobKeywordRaw) {
  const { includeExclude = 'None', jobKeywords: rawText = '' } = jobKeywordRaw || {};

  // If they chose "None", just return an empty array
  if (includeExclude === 'None') {
    return { includeExclude, jobKeywords: [] };
  }

  const seen = new Set();
  const keywords = [];

  rawText
    .split(/[,\r?\n]+/) // split on comma or newline (one or more)
    .map((token) => token.trim()) // trim whitespace around each
    .forEach((token) => {
      if (token && !seen.has(token)) {
        seen.add(token);
        keywords.push(token);
      }
    });

  return {
    includeExclude,
    jobKeywords: keywords,
  };
}

export function handleCriteriaInitialize(criteria, additionalOffersObj, context = 'offer') {
  /*
      This function is used to handle the initialization of the various forms of criteria data for offers, offer actions, offer wall ads, offer blocks and publisher pre-pings.
      The criteria field names for each entity should now be uniform across all entities. The differences lie in the data model.
      TODO: Unify all implementations of criteria data storage into a single table. Will require a migration. from all three tables to a new table or to the offer_criteria table.
     */

  if (!criteria) return null;
  console.log(criteria);
  let dayHourRestrictions =
    criteria.criteriaDayHourRestrictions !== null && criteria.criteriaDayHourRestrictions
      ? convertDailyRestrictionToArray(criteria.criteriaDayHourRestrictions)
      : criteria.dayHourRestrictions !== null && criteria.dayHourRestrictions
      ? convertDailyRestrictionToArray(criteria.dayHourRestrictions)
      : [];

  // Merge cap amounts from capConfiguration into dayHourRestrictions
  if (criteria.capConfiguration?.caps && dayHourRestrictions.length > 0) {
    const dowTypeToDay = {
      DOW_MONDAY: 'monday',
      DOW_TUESDAY: 'tuesday',
      DOW_WEDNESDAY: 'wednesday',
      DOW_THURSDAY: 'thursday',
      DOW_FRIDAY: 'friday',
      DOW_SATURDAY: 'saturday',
      DOW_SUNDAY: 'sunday',
    };

    // Build a map of day to cap amount
    const capAmountByDay = {};
    criteria.capConfiguration.caps.forEach((cap) => {
      const day = dowTypeToDay[cap.type];
      if (day && cap.amount != null) {
        capAmountByDay[day] = cap.amount;
      }
    });

    // Check for weekdays/weekends caps (if all weekdays have same amount, show on weekdays row)
    const weekdaysDays = ['monday', 'tuesday', 'wednesday', 'thursday', 'friday'];
    const weekendDays = ['saturday', 'sunday'];

    const weekdaysAmounts = weekdaysDays.map((d) => capAmountByDay[d]).filter((a) => a != null);
    const weekendAmounts = weekendDays.map((d) => capAmountByDay[d]).filter((a) => a != null);

    const allWeekdaysSame = weekdaysAmounts.length === 5 && new Set(weekdaysAmounts).size === 1;
    const allWeekendSame = weekendAmounts.length === 2 && new Set(weekendAmounts).size === 1;

    // Merge cap amounts into dayHourRestrictions entries
    dayHourRestrictions = dayHourRestrictions.map((entry) => {
      const day = entry.restrictionDay?.toLowerCase();
      let capAmount = null;

      if (day === 'weekdays' && allWeekdaysSame) {
        capAmount = weekdaysAmounts[0];
      } else if (day === 'weekends' && allWeekendSame) {
        capAmount = weekendAmounts[0];
      } else if (capAmountByDay[day] != null) {
        capAmount = capAmountByDay[day];
      }

      return capAmount != null ? { ...entry, capAmount } : entry;
    });
  }

  // suppressionRules is represented as a JSON string in the database for offers and multiple columns for offerCrtieria so we need to handle the separate cases accordingly
  const suppressionRulesOffer =
    criteria.suppressionRules &&
    criteria.suppressionRules !== null &&
    JSON.stringify(criteria.suppressionRules) !== '{}'
      ? JSON.parse(criteria.suppressionRules, function (k, v) {
          return typeof v === 'object' || isNaN(v) ? v : parseInt(v, 10);
        })
      : null;

  if (criteria == null) return {};
  const { internalPrePingConfiguration } = criteria;

  const cleanedJobKeyword = processInboundJobKeyword(criteria?.jobKeyword);

  const criteriaObjInit = {
    //Users
    triggersRegistration: criteria.triggersRegistration ?? false,
    criteriaTcpaLastOptInInterval:
      criteria.criteriaTcpaLastOptInInterval ??
      criteria.tcpaLastOptInInterval ??
      criteria.tcpaOptInRequiredOverLastInterval ??
      false,
    requiresOptInBeforeSend: criteria.requiresOptInBeforeSend ?? false,
    criteriaOfferRequiresOptIn: criteria.criteriaOfferRequiresOptIn ?? criteria.tcpaOptInRequired ?? false,
    criteriaPhoneHasBeenVerifiedWithBriteverify:
      criteria.criteriaPhoneHasBeenVerifiedWithBriteverify ?? criteria.briteVerifyPhoneValidated ?? false,
    criteriaEmailHasBeenVerifiedWithBriteverify:
      criteria.criteriaEmailHasBeenVerifiedWithBriteverify ?? criteria.briteVerifyEmailValidated ?? false,
    criteriaPhoneBlacklist: criteria.criteriaPhoneBlacklist ?? criteria.phoneBlacklist ?? false,
    englishBrowserLanguageRequired: criteria.englishBrowserLanguageRequired ?? false,

    criteriaMaxAge: criteria.criteriaMaxAge ?? criteria.maxAge ?? null,
    criteriaMinAge: criteria.criteriaMinAge ?? criteria.minAge ?? null,
    criteriaExcludeBrowserType: criteria.criteriaExcludeBrowserType ?? criteria.browserType ?? null,

    criteriaIncludeOrExcludeBrowsers:
      criteria.criteriaIncludeOrExcludeBrowsers ?? criteria.browsersIncludeExclude ?? null,
    criteriaIncludeOrExcludeDomains: criteria.criteriaIncludeOrExcludeDomains ?? criteria.domainsIncludeExclude ?? null,
    criteriaIncludeOrExcludeDevices: criteria.criteriaIncludeOrExcludeDevices ?? criteria.devicesIncludeExclude ?? null,
    criteriaIncludeOrExcludeOperatingSystems:
      criteria.criteriaIncludeOrExcludeOperatingSystems ?? criteria.operatingSystemIncludeExclude ?? null,

    criteriaNullSourceNewUserIncludeExclude:
      criteria.criteriaNullSourceNewUserIncludeExclude ?? criteria.nullSourceNewUserIncludeExclude ?? null,
    criteriaIncludeOrExcludeUserIdLastDigit:
      criteria.criteriaIncludeOrExcludeUserIdLastDigit ?? criteria.userIdLastDigitIncludeExclude ?? null,
    criteriaUserIdLastDigitString: criteria.criteriaUserIdLastDigitString ?? criteria.userIdLastDigitStringValues ?? [],

    criteriaAnuraScore: criteria.criteriaAnuraScore ?? criteria.anuraScore ?? null,
    criteriaBrowsers: criteria.criteriaBrowsers ?? criteria.browsers ?? null,
    criteriaDomains: criteria.criteriaDomains ?? criteria.domains ?? null,
    criteriaDevices: criteria.criteriaDevices ?? criteria.devices ?? null,
    criteriaOperatingSystems: criteria.criteriaOperatingSystems ?? criteria.operatingSystems ?? null,

    criteriaExcludeGender: criteria.criteriaExcludeGender ?? criteria.genderValue ?? null,
    criteriaIncludeOrExcludeStates: criteria.criteriaIncludeOrExcludeStates ?? criteria.statesIncludeExclude ?? null,
    criteriaIncludeOrExcludeZips: criteria.criteriaIncludeOrExcludeZips ?? criteria.zipsIncludeExclude ?? null,

    criteriaZips:
      criteria.criteriaZips && Array.isArray(criteria.criteriaZips)
        ? criteria.criteriaZips.join('\n')
        : criteria.zipsValues && Array.isArray(criteria.zipsValues)
        ? criteria.zipsValues.join('\n')
        : criteria.criteriaZips || !criteria.zipsValues
        ? criteria.criteriaZips || criteria.zipsValues
        : null,

    criteriaAibListIncludeExclude: criteria.criteriaAibListIncludeExclude ?? criteria.aibListIncludeExclude ?? null,
    criteriaAibList: criteria.criteriaAibList ?? criteria.aibList ?? null,
    domainAffiliateConfiguration:
      criteria.domainAffiliateConfiguration == null ||
      JSON.stringify(criteria.domainAffiliateConfiguration?.domainAffiliateEntries) === '[]'
        ? null
        : criteria.domainAffiliateConfiguration,
    previousRegistrationConfiguration:
      criteria.previousRegistrationConfiguration == null ||
      JSON.stringify(criteria.previousRegistrationConfiguration?.previousRegistrations) === '[]'
        ? null
        : {
            ...criteria.previousRegistrationConfiguration,
            andOrLogic: criteria.previousRegistrationConfiguration?.andOrLogic
              ? criteria.previousRegistrationConfiguration.andOrLogic.toUpperCase()
              : null,
            previousRegistrations:
              criteria.previousRegistrationConfiguration?.previousRegistrations?.map((reg) => ({
                ...reg,
                includeExcludeAction:
                  reg?.includeExcludeAction !== null
                    ? reg?.includeExcludeAction.charAt(0).toUpperCase() + reg?.includeExcludeAction.slice(1)
                    : null,
              })) ?? [],
          },

    ipqsConfiguration:
      criteria.ipqsConfiguration == null
        ? { shouldProcess: 'Disabled' }
        : {
            shouldProcess: criteria.ipqsConfiguration?.shouldProcess ? true : false,
            email: criteria.ipqsConfiguration?.email ?? null,
            phone: criteria.ipqsConfiguration?.phone ?? null,
            ip: criteria.ipqsConfiguration?.ip ?? null,
            fingerprint: criteria.ipqsConfiguration?.fingerprint ?? null,
          },

    criteriaUserDataIncludeExclude:
      criteria.criteriaUserDataIncludeExclude ?? criteria.requiredUserDataIncludeExclude ?? null,
    criteriaRequiredUserData:
      criteria.criteriaRequiredUserData && criteria.criteriaRequiredUserData !== null
        ? criteria.criteriaRequiredUserData?.split(',')
        : criteria.requiredUserDataValues ?? null,
    criteriaEmailDomains: criteria.criteriaEmailDomains?.split(',') ?? criteria.emailDomains ?? null,
    // User Criteria
    criteriaQuestion: criteria.criteriaQuestion ?? criteria.offerQuestion ?? null,
    criteriaQuestionAnswers: criteria.criteriaQuestionAnswers ?? criteria.offerQuestionAnswers ?? null,
    criteriaIncludeOrExcludeUserAttributes:
      criteria.criteriaIncludeOrExcludeUserAttributes ?? criteria.userAttributesIncludeExclude ?? null,
    userAttributeRules: criteria.userAttributeRules ?? null,
    criteriaIncludeOrExcludeUrlParameters:
      criteria.criteriaIncludeOrExcludeUrlParameters ?? criteria.urlParametersIncludeExclude ?? null,
    urlParameterRules: criteria.urlParameterRules ?? null,
    criteriaIncludeOrExcludeUserAgent:
      criteria.criteriaIncludeOrExcludeUserAgent ?? criteria.userAgentIncludeExclude ?? null,
    userAgentRules: criteria.userAgentRules ?? null,
    criteriaIncludeOrExcludeAffNames:
      criteria.criteriaIncludeOrExcludeAffNames ?? criteria.affiliateNamesIncludeExclude ?? null,
    criteriaAffNames:
      criteria.criteriaAffNames && criteria.criteriaAffNames !== null
        ? handleCriteriaAffNames(criteria.criteriaAffNames)
        : criteria.affiliateNames && criteria.affiliateNames != null
        ? handleCriteriaAffNames(criteria.affiliateNames)
        : [],
    affSubIDConfiguration: (() => {
      const cfg = criteria.affSubIDConfiguration;

      // If null/undefined, return object with empty andOrLogic so Field shows "Select Logic"
      if (cfg == null || typeof cfg !== 'object') {
        return {
          andOrLogic: '',
          subIdList: [],
        };
      }

      // Backend always returns new format: {"andOrLogic":"AND","subIdList":[...]}
      // If subIdList is empty, return object with empty andOrLogic
      if (!cfg.subIdList || (Array.isArray(cfg.subIdList) && cfg.subIdList.length === 0)) {
        return {
          andOrLogic: '',
          subIdList: [],
        };
      }

      // Process subIdList entries
      if (Array.isArray(cfg.subIdList) && cfg.subIdList.length > 0) {
        // Check if all entries are empty (no meaningful data)
        const hasAnyValidData = cfg.subIdList.some((entry) => {
          const hasIncludeExclude =
            entry.includeExclude && entry.includeExclude !== null && entry.includeExclude !== '';
          const hasAffiliate = entry.affiliate && entry.affiliate !== null && entry.affiliate !== '';
          const hasSubIds =
            entry.subIds &&
            ((Array.isArray(entry.subIds) && entry.subIds.length > 0) ||
              (typeof entry.subIds === 'string' && entry.subIds.trim() !== ''));
          return hasIncludeExclude || hasAffiliate || hasSubIds;
        });

        // If no valid data, treat as empty
        if (!hasAnyValidData) {
          return {
            andOrLogic: '',
            subIdList: [],
          };
        }

        // Transform subIds from array to string for textarea display in Redux-form
        return {
          ...cfg,
          andOrLogic: cfg.andOrLogic ? cfg.andOrLogic.toUpperCase() : null,
          subIdList: cfg.subIdList.map((entry) => {
            const rawSubIds = entry.subIds;
            const entrySubIds = Array.isArray(rawSubIds)
              ? rawSubIds.join('\n')
              : typeof rawSubIds === 'string'
              ? rawSubIds
              : null;
            return {
              ...entry,
              includeExclude: entry.includeExclude ?? null,
              affiliate: entry.affiliate ?? null,
              subIds: entrySubIds,
            };
          }),
        };
      }

      // Otherwise return empty structure
      return {
        andOrLogic: '',
        subIdList: [],
      };
    })(),
    qualityScoreCriteria: criteria.qualityScoreCriteria
      ? {
          minScore: criteria.qualityScoreCriteria.minScore ?? null,
          maxScore: criteria.qualityScoreCriteria.maxScore ?? null,
        }
      : null,
    criteriaStates: criteria.criteriaStates ?? criteria.statesValues ?? null,

    criteriaIncludeOrExcludeEmailDomains:
      criteria.criteriaIncludeOrExcludeEmailDomains ?? criteria.emailDomainsIncludeExclude ?? null,

    criteriaLandingPageGroupIncludeExclude:
      criteria.criteriaLandingPageGroupIncludeExclude ?? criteria.landingPageGroupIncludeExclude ?? null,
    criteriaLandingPageGroupIds: criteria.criteriaLandingPageGroupIds ?? criteria.landingPageGroupIds ?? null,
    criteriaLandingPageIncludeExclude:
      criteria.criteriaLandingPageIncludeExclude ?? criteria.landingPageIncludeExclude ?? null,
    criteriaLandingPageIds: criteria.criteriaLandingPageIds ?? criteria.landingPageIds ?? null,

    // Pre Ping
    payoutType: criteria.payoutType?.id ?? null,
    payoutAmount: criteria.payoutAmount ?? null,
    capAmount: criteria.capAmount ?? 0,
    capInterval: criteria.capInterval ?? null,
    capConfiguration: criteria.capConfiguration ?? null,
    capSegmentConfiguration:
      criteria.capSegmentConfiguration === null ||
      JSON.stringify(criteria.capSegmentConfiguration?.capSegments) === '[]'
        ? null
        : criteria.capSegmentConfiguration,
    domainLevelPrePing: criteria.domainLevelPrePing ?? null,
    prePing: criteria.prePing ?? criteria.prePingUrl ?? null,
    prePingMethod: criteria.prePingMethod ?? criteria.prePingType ?? null,
    prePingResultSessionCapture: criteria.prePingResultSessionCapture ?? null,
    externalPrePingProcessingEnabled: criteria.externalPrePingProcessingEnabled ?? false,
    prePingHandlerId: criteria.prePingHandlerId ?? criteria.prePingHandler ?? null,
    prePingCustomHttpStatus: criteria.prePingCustomHttpStatus ?? null,
    prePingBody: criteria.prePingBody ?? null,
    prePingTimeout: criteria.prePingTimeout ?? null,
    prePingHeader1: criteria.prePingHeader1 ?? null,
    prePingHeader2: criteria.prePingHeader2 ?? null,
    prePingHeader3: criteria.prePingHeader3 ?? null,

    // Suppression
    internalPrePingConfiguration:
      criteria.internalPrePingConfiguration == null ||
      JSON.stringify(criteria.internalPrePingConfiguration?.internalPrePings) === '[]'
        ? { andOrLogic: 'AND', internalPrePings: [{}] }
        : internalPrePingConfiguration,
    criteriaDailyLimit: criteria.criteriaDailyLimit ?? criteria.dailyLimit ?? null,
    criteriaDailyLimitAnswers: criteria.criteriaDailyLimitAnswers ?? criteria.dailyLimitAnswers ?? null,
    criteriaDayHourRestrictions: dayHourRestrictions,

    //Analysis
    randomFailureProbabilities: criteria.randomFailureProbabilities ?? null,

    // Quality Score Criteria - convert object structure to array structure for Redux Form
    // Handle both offers (qualityScoreCriteria) and offer actions (qualityScore)
    qualityScoreCriteria:
      criteria.qualityScoreCriteria || criteria.qualityScore
        ? {
            qualityScoreRange:
              (criteria.qualityScoreCriteria?.qualityScoreRange || criteria.qualityScore?.qualityScoreRange) &&
              typeof (criteria.qualityScoreCriteria?.qualityScoreRange || criteria.qualityScore?.qualityScoreRange) ===
                'object' &&
              !Array.isArray(
                criteria.qualityScoreCriteria?.qualityScoreRange || criteria.qualityScore?.qualityScoreRange
              )
                ? Object.keys(
                    criteria.qualityScoreCriteria?.qualityScoreRange || criteria.qualityScore?.qualityScoreRange
                  )
                    .sort((a, b) => {
                      // Sort by minScore (descending - highest first), then by key for consistency
                      const aData = (criteria.qualityScoreCriteria?.qualityScoreRange ||
                        criteria.qualityScore?.qualityScoreRange)[a];
                      const bData = (criteria.qualityScoreCriteria?.qualityScoreRange ||
                        criteria.qualityScore?.qualityScoreRange)[b];
                      const aMinScore = aData?.minScore;
                      const bMinScore = bData?.minScore;

                      // Convert to numbers for comparison
                      const aNum =
                        aMinScore !== null && aMinScore !== undefined && aMinScore !== '' ? parseInt(aMinScore) : null;
                      const bNum =
                        bMinScore !== null && bMinScore !== undefined && bMinScore !== '' ? parseInt(bMinScore) : null;

                      // If both are null/empty, maintain original order by key
                      if (aNum === null && bNum === null) {
                        return parseInt(a) - parseInt(b);
                      }

                      // If only a is null/empty, put it at the end
                      if (aNum === null) {
                        return 1;
                      }

                      // If only b is null/empty, put it at the end
                      if (bNum === null) {
                        return -1;
                      }

                      // Both have values, sort numerically (descending - highest first)
                      return bNum - aNum;
                    })
                    .map((key) => ({
                      name: `index_${key}`,
                      minScore: (criteria.qualityScoreCriteria?.qualityScoreRange ||
                        criteria.qualityScore?.qualityScoreRange)[key].minScore,
                      maxScore: (criteria.qualityScoreCriteria?.qualityScoreRange ||
                        criteria.qualityScore?.qualityScoreRange)[key].maxScore,
                      cap: (criteria.qualityScoreCriteria?.qualityScoreRange ||
                        criteria.qualityScore?.qualityScoreRange)[key].cap,
                      failureProbability: (criteria.qualityScoreCriteria?.qualityScoreRange ||
                        criteria.qualityScore?.qualityScoreRange)[key].failureProbability,
                      validMultipliers: (criteria.qualityScoreCriteria?.qualityScoreRange ||
                        criteria.qualityScore?.qualityScoreRange)[key].validMultipliers,
                      dayFilters:
                        (criteria.qualityScoreCriteria?.qualityScoreRange || criteria.qualityScore?.qualityScoreRange)[
                          key
                        ].dayFilters ||
                        ((criteria.qualityScoreCriteria?.qualityScoreRange || criteria.qualityScore?.qualityScoreRange)[
                          key
                        ].dayFilter
                          ? [
                              (criteria.qualityScoreCriteria?.qualityScoreRange ||
                                criteria.qualityScore?.qualityScoreRange)[key].dayFilter,
                            ]
                          : []),
                    }))
                : (() => {
                    // Fallback: create a single row from legacy min/max if present
                    const legacyMin =
                      criteria.qualityScoreCriteria?.minScore ??
                      criteria.qualityScore?.minScore ??
                      criteria.minScore ??
                      null;
                    const legacyMax =
                      criteria.qualityScoreCriteria?.maxScore ??
                      criteria.qualityScore?.maxScore ??
                      criteria.maxScore ??
                      null;
                    if (legacyMin !== null || legacyMax !== null) {
                      return [
                        {
                          name: 'index_0',
                          minScore: legacyMin,
                          maxScore: legacyMax,
                          cap: null,
                          failureProbability: 0,
                          validMultipliers: 0,
                          dayFilters: [],
                        },
                      ];
                    }
                    return (
                      criteria.qualityScoreCriteria?.qualityScoreRange || criteria.qualityScore?.qualityScoreRange || []
                    );
                  })(),
            bypass:
              (criteria.qualityScoreCriteria?.bypass || criteria.qualityScore?.bypass) &&
              typeof (criteria.qualityScoreCriteria?.bypass || criteria.qualityScore?.bypass) === 'object' &&
              !Array.isArray(criteria.qualityScoreCriteria?.bypass || criteria.qualityScore?.bypass)
                ? Object.keys(criteria.qualityScoreCriteria?.bypass || criteria.qualityScore?.bypass)
                    .sort((a, b) => parseInt(a) - parseInt(b))
                    .map((key) => ({
                      name: `index_${key}`,
                      bypass_affiliate:
                        (criteria.qualityScoreCriteria?.bypass || criteria.qualityScore?.bypass)[key]
                          .bypass_affiliate || [],
                      cap: (criteria.qualityScoreCriteria?.bypass || criteria.qualityScore?.bypass)[key].cap,
                      failureProbability: (criteria.qualityScoreCriteria?.bypass || criteria.qualityScore?.bypass)[key]
                        .failureProbability,
                    }))
                : criteria.qualityScoreCriteria?.bypass || criteria.qualityScore?.bypass || [],
            cap_reset: {
              resetInterval: 'daily',
              resetDayOfWeek: '',
              resetDayOfMonth: '',
              resetDate: '',
            },
          }
        : null,

    // Job Keyword
    jobKeyword: cleanedJobKeyword,
  };
  console.log('Criteria', criteriaObjInit);
  return criteriaObjInit;
}

function processTextareaValues(criteriaZips) {
  let zipsValues =
    criteriaZips && typeof criteriaZips !== 'string'
      ? criteriaZips
      : typeof criteriaZips === 'string'
      ? criteriaZips.split(/\D/)
      : [];

  return zipsValues
    .map((zip) =>
      zip
        .split('')
        .map((char) => char.replace(/\D/g, ''))
        .join('')
    )
    .filter((el) => el !== '');
}

// Build capConfiguration from criteriaDayHourRestrictions array (for offer actions)
// Handles weekdays/weekends expansion - if weekdays has a cap, apply to Mon-Fri; if weekends has a cap, apply to Sat-Sun
// Also handles Monthly/Lifetime cap intervals by creating corresponding cap instances
function buildCapConfigurationFromDayPartingArray(dayPartingArray, parsedCriteriaConfig, isNewPanel = false) {
  const capAmount = parsedCriteriaConfig?.capAmount ?? null;
  const capInterval = parsedCriteriaConfig?.capInterval ?? null;
  // Map capInterval string values to CapTypes (excluding Daily - handled separately )
  const intervalToCapType = {
    Monthly: 'MONTHLY',
    Lifetime: 'LIFETIME',
  };

  // Build Monthly/Lifetime cap instance from current values
  let intervalCap = null;
  if (capInterval && intervalToCapType[capInterval] && capAmount != null && capAmount !== '' && capAmount !== 0) {
    intervalCap = {
      type: intervalToCapType[capInterval],
      amount: Number(capAmount),
      restriction: null,
    };
  }

  // If the new criteria panel is active, its capConfiguration is authoritative — including null
  // (meaning the user explicitly cleared all caps). Never fall back to the legacy path in this case.
  const panelCaps = parsedCriteriaConfig?.capConfiguration?.caps;
  if (isNewPanel || Array.isArray(panelCaps)) {
    // Keep DOW caps from the panel as-is; replace any existing interval cap with the freshly built one
    const dowCaps = Array.isArray(panelCaps) ? panelCaps.filter((cap) => cap.type && cap.type.startsWith('DOW_')) : [];
    const allCaps = [...dowCaps, ...(intervalCap ? [intervalCap] : [])];
    return allCaps.length > 0 ? { caps: allCaps } : null;
  }

  // Legacy path: rebuild DOW caps from the dayPartingArray (old criteria component)
  const dayToDowType = {
    monday: 'DOW_MONDAY',
    tuesday: 'DOW_TUESDAY',
    wednesday: 'DOW_WEDNESDAY',
    thursday: 'DOW_THURSDAY',
    friday: 'DOW_FRIDAY',
    saturday: 'DOW_SATURDAY',
    sunday: 'DOW_SUNDAY',
  };

  // Build lookup map from existing DOW caps in parsedCriteriaConfig (fallback for offers)
  const existingCaps = parsedCriteriaConfig?.capConfiguration?.caps || [];
  const existingCapsMap = new Map();
  existingCaps.forEach((cap) => {
    if (cap.type && cap.type.startsWith('DOW_')) {
      existingCapsMap.set(cap.type, { ...cap });
    }
  });

  // If no day parting array, there are no DOW caps - only return interval cap if present
  if (!dayPartingArray || !Array.isArray(dayPartingArray) || dayPartingArray.length === 0) {
    if (intervalCap) {
      return { caps: [intervalCap] };
    }
    return null;
  }

  // Weekdays and weekends mapping
  const weekdaysDays = ['monday', 'tuesday', 'wednesday', 'thursday', 'friday'];
  const weekendDays = ['saturday', 'sunday'];

  // Build caps from dayPartingArray
  const newDowCaps = [];

  // Convert restriction to CapRestriction format
  const toCapRestriction = (entry) => {
    if (entry?.restriction === 'Partially Restricted' && entry.restrictionStart && entry.restrictionEnd) {
      return {
        type: 'PARTIAL',
        starts: entry.restrictionStart,
        ends: entry.restrictionEnd,
      };
    }
    return null;
  };

  // Helper to get cap amount - prioritize dayPartingArray (entry.capAmount), fallback to parsedCriteriaConfig
  // Returns null if amount is 0, '', or null (no cap instance should be created)
  const getCapAmount = (dowType, entry) => {
    // First check entry.capAmount (dayPartingArray - source of truth for offer actions)
    if (entry.capAmount != null && entry.capAmount !== '' && entry.capAmount !== 0) {
      return Number(entry.capAmount);
    }
    // Fallback to existingCapsMap (from parsedCriteriaConfig.capConfiguration.caps - for offers)
    const existingCap = existingCapsMap.get(dowType);
    if (existingCap && existingCap.amount != null && existingCap.amount !== '' && existingCap.amount !== 0) {
      return Number(existingCap.amount);
    }
    return null;
  };

  dayPartingArray.forEach((entry) => {
    const { restrictionDay, restriction } = entry;

    // Skip if Unavailable - no cap should be set for unavailable days
    if (restriction === 'Unavailable') return;

    const capRestriction = toCapRestriction(entry);

    if (restrictionDay === 'weekdays') {
      // Apply cap to all weekdays
      weekdaysDays.forEach((day) => {
        const dowType = dayToDowType[day];
        const amount = getCapAmount(dowType, entry);
        if (amount != null) {
          newDowCaps.push({
            type: dowType,
            amount,
            restriction: capRestriction,
          });
        }
      });
    } else if (restrictionDay === 'weekends') {
      // Apply cap to all weekend days
      weekendDays.forEach((day) => {
        const dowType = dayToDowType[day];
        const amount = getCapAmount(dowType, entry);
        if (amount != null) {
          newDowCaps.push({
            type: dowType,
            amount,
            restriction: capRestriction,
          });
        }
      });
    } else if (dayToDowType[restrictionDay]) {
      // Single day
      const dowType = dayToDowType[restrictionDay];
      const amount = getCapAmount(dowType, entry);
      if (amount != null) {
        newDowCaps.push({
          type: dowType,
          amount,
          restriction: capRestriction,
        });
      }
    }
  });

  // Dedupe by type (last one wins if duplicates)
  const dowCapMap = {};
  newDowCaps.forEach((cap) => {
    dowCapMap[cap.type] = { ...cap }; // Clone to avoid reference issues
  });

  const allCaps = [...Object.values(dowCapMap), ...(intervalCap ? [{ ...intervalCap }] : [])];

  if (allCaps.length === 0) return null;

  return { caps: allCaps };
}

export function handleCriteriaSubmit(values, handlers, context = 'offer') {
  /*
      This function is used to handle/sanitize the various forms of criteria data for offers, offer actions, offer wall ads, offer blocks and publisher pre-pings.
      The offer's criteria is stored directly on the offer table, where as the offer action, offer wall ad, offer block criteria data is stored on the offer_criteria table.
      The publisher pre-ping criteria is stored on the publisher_pre_ping_criteria table.
      TODO: Unify all implementations of criteria data storage into a single table. Will require a migration. from all three tables to a new table or to the offer_criteria table.
     */

  // Parse new criteria configuration if it exists
  // For offers, this comes from values.criteria?.criteriaConfiguration (nested JSON string)
  // For others, the data is at the top level of values (values.capAmount, values.capConfiguration, etc.)
  let parsedCriteriaConfig = {};
  try {
    if (values.criteria?.criteriaConfiguration) {
      parsedCriteriaConfig = JSON.parse(values.criteria.criteriaConfiguration);
      console.log('Parsed criteria configuration:', parsedCriteriaConfig);
    } else {
      // For others, use top-level values
      parsedCriteriaConfig = {
        capAmount: values.capAmount ?? null,
        capInterval: values.capInterval ?? null,
        capConfiguration: values.capConfiguration ?? null,
      };
    }
  } catch (error) {
    console.warn('Failed to parse criteriaConfiguration:', error);
    parsedCriteriaConfig = {};
  }

  let {
    criteriaAffNames,
    criteriaIncludeOrExcludeAffNames,
    criteriaIncludeOrExcludeUserAttributes,
    criteriaIncludeOrExcludeUrlParameters,
    criteriaIncludeOrExcludeUserAgent,
    criteriaNullSourceNewUserIncludeExclude,
    criteriaIncludeOrExcludeUserIdLastDigit,
    criteriaReturnFrequencyIncludeExclude,
    internalPrePingConfiguration,
  } = values;

  let criteriaQuestionValue = null;
  if (values.criteriaQuestion !== undefined && values.criteriaQuestion !== null) {
    if (values.criteriaQuestion.name !== undefined && values.criteriaQuestion.name !== null) {
      criteriaQuestionValue = values.criteriaQuestion.name === null ? null : values.criteriaQuestion.name;
    } else {
      criteriaQuestionValue = values.criteriaQuestion === null ? null : values.criteriaQuestion;
    }
  }

  if (
    criteriaIncludeOrExcludeUserAttributes === null ||
    !criteriaIncludeOrExcludeUserAttributes ||
    criteriaIncludeOrExcludeUserAttributes === 'None'
  ) {
    criteriaIncludeOrExcludeUserAttributes = null;
  }

  if (
    criteriaIncludeOrExcludeUrlParameters === null ||
    !criteriaIncludeOrExcludeUrlParameters ||
    criteriaIncludeOrExcludeUrlParameters === 'None'
  ) {
    criteriaIncludeOrExcludeUrlParameters = null;
  }

  if (
    criteriaIncludeOrExcludeUserAgent === null ||
    !criteriaIncludeOrExcludeUserAgent ||
    criteriaIncludeOrExcludeUserAgent === 'None'
  ) {
    criteriaIncludeOrExcludeUserAgent = null;
  }

  const isDayHoursRestrictions =
    values.criteriaDayHourRestrictions && values.criteriaDayHourRestrictions.length > 0
      ? Object.keys(values.criteriaDayHourRestrictions[0]).length > 0
      : null;

  let prePingHandler = null;
  let prePingHandlerValue =
    typeof values.prePingHandlerId === 'string'
      ? findIdFromName(handlers, values.prePingHandlerId)
      : values.prePingHandlerId;
  if (prePingHandlerValue === undefined) prePingHandler = null;
  else {
    prePingHandler =
      typeof values.prePingHandlerId === 'string'
        ? { id: prePingHandlerValue, name: values.prePingHandlerId }
        : prePingHandlerValue;
  }

  const prePingMethod =
    values.prePingMethod && values.prePingMethod !== null && values.prePingMethod !== 'NONE'
      ? values.prePingMethod
      : null;

  let zipsValues = processTextareaValues(values.criteriaZips);
  const cleanedJobKeyword = processOutboundJobKeyword(values?.jobKeyword);

  if (internalPrePingConfiguration && internalPrePingConfiguration.internalPrePings.length > 0) {
    if (!internalPrePingConfiguration.andOrLogic) internalPrePingConfiguration.andOrLogic = 'AND';
    const validSuppressionTypes = ['days', 'hours', 'minutes'];
    internalPrePingConfiguration.internalPrePings = internalPrePingConfiguration.internalPrePings
      .map((internalPrePing) => {
        if (internalPrePing.additionalOffers && internalPrePing.additionalOffers.length > 0) {
          internalPrePing.additionalOffers = parseAdditionalOffers(internalPrePing.additionalOffers);
        } else if (internalPrePing.additionalOfferActions && internalPrePing.additionalOfferActions.length > 0) {
          internalPrePing.additionalOfferActions = parseAdditionalOffers(internalPrePing.additionalOfferActions);
        }
        return internalPrePing;
      })
      .filter(
        (internalPrePing) =>
          internalPrePing?.prePingSuppressionType &&
          validSuppressionTypes.includes(internalPrePing.prePingSuppressionType.toLowerCase())
      );
  }

  const createIpqsConfiguration = () => {
    if (values.ipqsConfiguration?.shouldProcess !== 'Enabled') {
      return { shouldProcess: false };
    }

    const isValid = [
      values.ipqsConfiguration?.email,
      values.ipqsConfiguration?.phone,
      values.ipqsConfiguration?.ip,
      values.ipqsConfiguration?.fingerprint,
    ].some((val) => typeof val === 'number');
    if (!isValid) {
      return { shouldProcess: false };
    }

    return {
      shouldProcess: true,
      email: values.ipqsConfiguration?.email,
      phone: values.ipqsConfiguration?.phone,
      ip: values.ipqsConfiguration?.ip,
      fingerprint: values.ipqsConfiguration?.fingerprint,
    };
  };

  const criteriaObj = {
    aibListIncludeExclude:
      !values.criteriaAibListIncludeExclude || values.criteriaAibListIncludeExclude === 'None'
        ? null
        : values.criteriaAibListIncludeExclude,
    aibList:
      !values.criteriaAibListIncludeExclude ||
      values.criteriaAibListIncludeExclude === 'None' ||
      values.criteriaAibList === null
        ? null
        : values.criteriaAibList,
    affiliateNames: criteriaIncludeOrExcludeAffNames === 'None' ? [] : handleCriteriaAffNames(criteriaAffNames),
    affiliateNamesIncludeExclude: criteriaIncludeOrExcludeAffNames === 'None' ? null : criteriaIncludeOrExcludeAffNames,
    ageEnabled: values.criteriaMinAge || values.criteriaMaxAge ? true : false,
    anuraScore: values.criteriaAnuraScore === '' ? null : values.criteriaAnuraScore,
    briteVerifyEmailValidated:
      parsedCriteriaConfig.briteVerifyEmailValidated ?? values.criteriaEmailHasBeenVerifiedWithBriteverify,
    briteVerifyPhoneValidated:
      parsedCriteriaConfig.briteVerifyPhoneValidated ?? values.criteriaPhoneHasBeenVerifiedWithBriteverify,
    englishBrowserLanguageRequired: values.englishBrowserLanguageRequired,
    browserType: values.criteriaExcludeBrowserType === 'null' ? null : values.criteriaExcludeBrowserType,
    browsers: values.criteriaIncludeOrExcludeBrowsers === 'None' ? null : values.criteriaBrowsers,
    browsersIncludeExclude:
      values.criteriaIncludeOrExcludeBrowsers === 'None' ? null : values.criteriaIncludeOrExcludeBrowsers,
    capInterval: (() => {
      const interval = parsedCriteriaConfig.capInterval ?? values.capInterval;
      return interval && interval !== 'Select' && interval !== 'None' ? interval : null;
    })(),
    capAmount: (() => {
      const interval = parsedCriteriaConfig.capInterval ?? values.capInterval;
      const amount = parsedCriteriaConfig.capAmount ?? values.capAmount;
      return interval && interval !== 'Select' && interval !== 'None' ? parseInt(amount) || null : null;
    })(),
    capConfiguration: buildCapConfigurationFromDayPartingArray(
      values.criteriaDayHourRestrictions,
      parsedCriteriaConfig,
      !!values.criteria?.criteriaConfiguration
    ),
    capSegmentConfiguration:
      !values.capSegmentConfiguration || JSON.stringify(values.capSegmentConfiguration?.capSegments) === '[]'
        ? null
        : values.capSegmentConfiguration,
    criteriaAibListIncludeExclude:
      !values.criteriaAibListIncludeExclude || values.criteriaAibListIncludeExclude === 'None'
        ? null
        : values.criteriaAibListIncludeExclude,
    criteriaAibList:
      !values.criteriaAibListIncludeExclude ||
      values.criteriaAibListIncludeExclude === 'None' ||
      values.criteriaAibList === null
        ? null
        : values.criteriaAibList,
    criteriaAnuraScore: values.criteriaAnuraScore === '' ? null : values.criteriaAnuraScore,
    criteriaBrowsers: values.criteriaIncludeOrExcludeBrowsers === 'None' ? null : values.criteriaBrowsers,
    criteriaDailyLimit: values.criteriaDailyLimit ?? null,
    criteriaDailyLimitAnswers: values.criteriaDailyLimitAnswers?.length ? values.criteriaDailyLimitAnswers : null,
    criteriaDayHourRestrictions: isDayHoursRestrictions
      ? JSON.stringify(convertDailyRestrictionToJson(values.criteriaDayHourRestrictions))
      : null,
    criteriaDevices: values.criteriaIncludeOrExcludeDevices === 'None' ? null : values.criteriaDevices,
    criteriaDomains:
      values.criteriaIncludeOrExcludeDomains === 'None' ? null : handleCriteriaDomains(values.criteriaDomains),
    criteriaEmailDomains:
      values.criteriaIncludeOrExcludeEmailDomains === 'None'
        ? null
        : Array.isArray(values.criteriaEmailDomains) && values.criteriaEmailDomains.length
        ? values.criteriaEmailDomains.join(',')
        : typeof values.criteriaEmailDomains === 'string'
        ? values.criteriaEmailDomains
        : null,
    criteriaExcludeBrowserType: values.criteriaExcludeBrowserType === 'null' ? null : values.criteriaExcludeBrowserType,
    criteriaExcludeGender: values.criteriaExcludeGender === 'null' ? null : values.criteriaExcludeGender,
    criteriaIncludeOrExcludeAffNames:
      criteriaIncludeOrExcludeAffNames === 'None' ? null : criteriaIncludeOrExcludeAffNames,
    criteriaIncludeOrExcludeBrowsers:
      values.criteriaIncludeOrExcludeBrowsers === 'None' ? null : values.criteriaIncludeOrExcludeBrowsers,
    criteriaIncludeOrExcludeDevices:
      values.criteriaIncludeOrExcludeDevices === 'None' ? null : values.criteriaIncludeOrExcludeDevices,
    criteriaIncludeOrExcludeDomains:
      values.criteriaIncludeOrExcludeDomains === 'None' ? null : values.criteriaIncludeOrExcludeDomains,
    criteriaIncludeOrExcludeEmailDomains:
      values.criteriaIncludeOrExcludeEmailDomains === 'None' ? null : values.criteriaIncludeOrExcludeEmailDomains,
    criteriaIncludeOrExcludeOperatingSystems:
      values.criteriaIncludeOrExcludeOperatingSystems === 'None'
        ? null
        : values.criteriaIncludeOrExcludeOperatingSystems,
    criteriaIncludeOrExcludeStates:
      values.criteriaIncludeOrExcludeStates === 'None' ? null : values.criteriaIncludeOrExcludeStates,
    criteriaIncludeOrExcludeUserAttributes,
    criteriaIncludeOrExcludeUrlParameters,
    criteriaIncludeOrExcludeUserAgent,
    criteriaIncludeOrExcludeZips:
      values.criteriaIncludeOrExcludeZips === 'None' ? null : values.criteriaIncludeOrExcludeZips,
    criteriaNullSourceNewUserIncludeExclude:
      criteriaNullSourceNewUserIncludeExclude === 'None' ? null : criteriaNullSourceNewUserIncludeExclude,
    criteriaOperatingSystems:
      values.criteriaIncludeOrExcludeOperatingSystems === 'None' ? null : values.criteriaOperatingSystems,
    criteriaPhoneBlacklist: values.criteriaPhoneBlacklist || false,

    criteriaAffNames:
      !criteriaAffNames || criteriaIncludeOrExcludeAffNames === 'None' ? [] : handleCriteriaAffNames(criteriaAffNames),

    criteriaIncludeOrExcludeUserIdLastDigit:
      !criteriaIncludeOrExcludeUserIdLastDigit || criteriaIncludeOrExcludeUserIdLastDigit === 'None'
        ? null
        : criteriaIncludeOrExcludeUserIdLastDigit,
    criteriaLandingPageGroupIncludeExclude:
      !values.criteriaLandingPageGroupIncludeExclude || values.criteriaLandingPageGroupIncludeExclude === 'None'
        ? null
        : values.criteriaLandingPageGroupIncludeExclude,
    criteriaLandingPageGroupIds:
      !values.criteriaLandingPageGroupIncludeExclude || values.criteriaLandingPageGroupIncludeExclude === 'None'
        ? null
        : values.criteriaLandingPageGroupIds &&
          Array.isArray(values.criteriaLandingPageGroupIds) &&
          values.criteriaLandingPageGroupIds.length > 0
        ? values.criteriaLandingPageGroupIds.map((item) => Number(item?.id || item))
        : values.criteriaLandingPageGroupIds,

    criteriaLandingPageIncludeExclude:
      !values.criteriaLandingPageIncludeExclude || values.criteriaLandingPageIncludeExclude === 'None'
        ? null
        : values.criteriaLandingPageIncludeExclude,
    criteriaLandingPageIds:
      !values.criteriaLandingPageIncludeExclude || values.criteriaLandingPageIncludeExclude === 'None'
        ? null
        : values.criteriaLandingPageIds &&
          Array.isArray(values.criteriaLandingPageIds) &&
          values.criteriaLandingPageIds.length > 0
        ? values.criteriaLandingPageIds.map((item) => Number(item?.id || item))
        : values.criteriaLandingPageIds,
    criteriaMaxAge: values.criteriaMaxAge && parseInt(values.criteriaMaxAge),
    criteriaMinAge: values.criteriaMinAge && parseInt(values.criteriaMinAge),

    criteriaOfferRequiresOptIn: values.criteriaOfferRequiresOptIn,
    criteriaQuestion: criteriaQuestionValue,
    criteriaQuestionAnswers: values.criteriaQuestionAnswers,
    criteriaUserDataIncludeExclude:
      !values.criteriaUserDataIncludeExclude || values.criteriaUserDataIncludeExclude === 'None'
        ? null
        : values.criteriaUserDataIncludeExclude,
    criteriaRequiredUserData:
      !values.criteriaUserDataIncludeExclude || values.criteriaUserDataIncludeExclude === 'None'
        ? null
        : Array.isArray(values.criteriaRequiredUserData) && values.criteriaRequiredUserData.length
        ? values.criteriaRequiredUserData.join(',')
        : typeof values.criteriaRequiredUserData === 'string'
        ? values.criteriaRequiredUserData
        : null,
    criteriaReturnFrequencyDomains:
      !criteriaReturnFrequencyIncludeExclude || criteriaReturnFrequencyIncludeExclude === 'None'
        ? []
        : values.criteriaReturnFrequencyDomains,
    criteriaReturnFrequencyIncludeExclude:
      !criteriaReturnFrequencyIncludeExclude || criteriaReturnFrequencyIncludeExclude === 'None'
        ? null
        : criteriaReturnFrequencyIncludeExclude,
    criteriaReturnFrequencyMax:
      !criteriaReturnFrequencyIncludeExclude || criteriaReturnFrequencyIncludeExclude === 'None'
        ? null
        : values.criteriaReturnFrequencyMax,
    criteriaReturnFrequencyMin:
      !criteriaReturnFrequencyIncludeExclude || criteriaReturnFrequencyIncludeExclude === 'None'
        ? null
        : values.criteriaReturnFrequencyMin,
    criteriaStates:
      values.criteriaIncludeOrExcludeStates && values.criteriaIncludeOrExcludeStates !== 'None' && values.criteriaStates
        ? values.criteriaStates
        : null,
    criteriaUserIdLastDigitString:
      !criteriaIncludeOrExcludeUserIdLastDigit ||
      criteriaIncludeOrExcludeUserIdLastDigit === 'None' ||
      values.criteriaUserIdLastDigitString === null
        ? []
        : values.criteriaUserIdLastDigitString,
    criteriaZips:
      values.criteriaIncludeOrExcludeZips && values.criteriaIncludeOrExcludeZips !== 'None' && values.criteriaZips
        ? values.criteriaZips
        : null,
    dailyLimit: values.dailyLimit ?? null,
    dailyLimitAnswers:
      values.criteriaDailyLimitAnswers != null && values.criteriaDailyLimitAnswers.length > 0
        ? values.criteriaDailyLimitAnswers
        : null,
    dayHourRestrictions: isDayHoursRestrictions
      ? JSON.stringify(convertDailyRestrictionToJson(values.criteriaDayHourRestrictions))
      : null,
    devices:
      values.criteriaIncludeOrExcludeDevices === null ||
      values.criteriaIncludeOrExcludeDevices === 'None' ||
      values.criteriaDevices === null
        ? null
        : values.criteriaDevices,
    devicesIncludeExclude:
      !values.criteriaIncludeOrExcludeDevices || values.criteriaIncludeOrExcludeDevices === 'None'
        ? null
        : values.criteriaIncludeOrExcludeDevices,
    domains:
      values.criteriaIncludeOrExcludeDomains === null ||
      values.criteriaIncludeOrExcludeDomains === 'None' ||
      values.criteriaDomains === null
        ? null
        : handleCriteriaDomains(values.criteriaDomains),
    domainsIncludeExclude:
      values.criteriaIncludeOrExcludeDomains === 'None' ? null : values.criteriaIncludeOrExcludeDomains,
    domainAffiliateConfiguration:
      !values.domainAffiliateConfiguration ||
      JSON.stringify(values.domainAffiliateConfiguration?.domainAffiliateEntries) === '[]' ||
      (values.domainAffiliateConfiguration?.domainAffiliateEntries.length === 1 &&
        values.domainAffiliateConfiguration?.domainAffiliateEntries[0]?.includeExcludeAction === null) ||
      (values.domainAffiliateConfiguration?.domainAffiliateEntries?.length === 1 &&
        values.domainAffiliateConfiguration?.domainAffiliateEntries[0]?.includeExcludeAction === 'None')
        ? null
        : handleCriteriaDomainAffiliateNames(values.domainAffiliateConfiguration),
    previousRegistrationConfiguration:
      !values.previousRegistrationConfiguration ||
      JSON.stringify(values.previousRegistrationConfiguration?.previousRegistrations) === '[]' ||
      !values.previousRegistrationConfiguration.andOrLogic ||
      values.previousRegistrationConfiguration?.andOrLogic === null ||
      values.previousRegistrationConfiguration?.andOrLogic.toLowerCase() === 'select'
        ? null
        : {
            ...values.previousRegistrationConfiguration,
            previousRegistrations:
              values.previousRegistrationConfiguration?.previousRegistrations?.map((reg) => {
                const result = {
                  ...reg,
                  includeExcludeAction: reg?.includeExcludeAction?.toLowerCase(),
                };

                if (reg?.domains) {
                  result.domains = handleCriteriaDomains(reg.domains);
                }

                if (reg?.affiliates) {
                  result.affiliates = handleCriteriaDomains(reg.affiliates);
                }

                return result;
              }) ?? [],
          },

    ipqsConfiguration: createIpqsConfiguration(),

    internalPrePingConfiguration:
      !internalPrePingConfiguration || JSON.stringify(internalPrePingConfiguration?.internalPrePings) === '[]'
        ? null
        : internalPrePingConfiguration,

    emailDomainsIncludeExclude:
      !values.criteriaIncludeOrExcludeEmailDomains || values.criteriaIncludeOrExcludeEmailDomains === 'None'
        ? null
        : values.criteriaIncludeOrExcludeEmailDomains,
    domainLevelPrePing:
      !values.domainLevelPrePing || values.domainLevelPrePing.name === 'None' ? null : values.domainLevelPrePing,
    emailDomains:
      values.criteriaIncludeOrExcludeEmailDomains === null ||
      values.criteriaIncludeOrExcludeEmailDomains === 'None' ||
      values.criteriaEmailDomains === null
        ? null
        : values.criteriaEmailDomains && values.criteriaEmailDomains !== null && values.criteriaEmailDomains.length > 0
        ? values.criteriaEmailDomains
        : null,
    genderEnabled: !values.criteriaExcludeGender || values.criteriaExcludeGender === 'null' ? false : true,
    genderValue:
      !values.criteriaExcludeGender || values.criteriaExcludeGender === 'null' ? null : values.criteriaExcludeGender,
    landingPageGroupIncludeExclude:
      !values.criteriaLandingPageGroupIncludeExclude || values.criteriaLandingPageGroupIncludeExclude === 'None'
        ? null
        : values.criteriaLandingPageGroupIncludeExclude,
    landingPageGroupIds:
      !values.criteriaLandingPageGroupIncludeExclude || values.criteriaLandingPageGroupIncludeExclude === 'None'
        ? null
        : values.criteriaLandingPageGroupIds &&
          Array.isArray(values.criteriaLandingPageGroupIds) &&
          values.criteriaLandingPageGroupIds.length > 0
        ? values.criteriaLandingPageGroupIds.map((item) => (item?.id ? item.id : item))
        : values.criteriaLandingPageGroupIds,

    landingPageIncludeExclude:
      !values.criteriaLandingPageIncludeExclude || values.criteriaLandingPageIncludeExclude === 'None'
        ? null
        : values.criteriaLandingPageIncludeExclude,
    landingPageIds:
      !values.criteriaLandingPageIncludeExclude || values.criteriaLandingPageIncludeExclude === 'None'
        ? null
        : values.criteriaLandingPageIds &&
          Array.isArray(values.criteriaLandingPageIds) &&
          values.criteriaLandingPageIds.length > 0
        ? values.criteriaLandingPageIds.map((item) => (item?.id ? item.id : item))
        : values.criteriaLandingPageIds,
    maxAge: values.criteriaMaxAge && parseInt(values.criteriaMaxAge),
    minAge: values.criteriaMinAge && parseInt(values.criteriaMinAge),
    nullSourceNewUserIncludeExclude:
      !values.criteriaNullSourceNewUserIncludeExclude || values.criteriaNullSourceNewUserIncludeExclude === 'None'
        ? null
        : values.criteriaNullSourceNewUserIncludeExclude,
    offerQuestion: values.criteriaQuestion,
    offerQuestionAnswers: values.criteriaQuestionAnswers,
    operatingSystemIncludeExclude:
      !values.criteriaIncludeOrExcludeOperatingSystems || values.criteriaIncludeOrExcludeOperatingSystems === 'None'
        ? null
        : values.criteriaIncludeOrExcludeOperatingSystems,
    operatingSystems:
      values.criteriaIncludeOrExcludeOperatingSystems === null ||
      values.criteriaIncludeOrExcludeOperatingSystems === 'None' ||
      values.criteriaOperatingSystems === null
        ? null
        : values.criteriaOperatingSystems,
    payoutAmount:
      values.payoutType !== null &&
      values.payoutType !== '0' &&
      values.payoutType !== '4' &&
      values.payoutType !== '6' && // CPC-PP: no fixed amount
      values.payoutType !== 6
        ? values.payoutAmount
        : null,
    payoutType: values.payoutType && values.payoutType !== '0' ? { id: values.payoutType } : null,
    phoneBlacklist: values.criteriaPhoneBlacklist,
    prePing: prePingMethod === null ? null : values.prePing,
    prePingBody: prePingMethod === null ? null : values.prePingBody,
    prePingCustomHttpStatus: prePingMethod === null ? null : values.prePingCustomHttpStatus,
    prePingHandler: prePingMethod === null ? null : prePingHandler,
    prePingHandlerId: prePingMethod === null ? null : prePingHandler,
    prePingTimeout: prePingMethod === null ? null : values.prePingTimeout,
    prePingHeader1: prePingMethod === null ? null : values.prePingHeader1,
    prePingHeader2: prePingMethod === null ? null : values.prePingHeader2,
    prePingHeader3: prePingMethod === null ? null : values.prePingHeader3,
    prePingMethod,
    prePingResultSessionCapture: prePingMethod === null ? null : values.prePingResultSessionCapture,
    externalPrePingProcessingEnabled: values.externalPrePingProcessingEnabled,
    prePingType: prePingMethod,
    prePingUrl: prePingMethod === null ? null : values.prePing,
    requiredUserDataIncludeExclude: values.criteriaUserDataIncludeExclude,
    requiredUserDataValues:
      values.criteriaRequiredUserData && values.criteriaRequiredUserData.length > 0
        ? values.criteriaRequiredUserData
        : null,
    returnFrequencyIncludeExclude:
      !criteriaReturnFrequencyIncludeExclude || criteriaReturnFrequencyIncludeExclude === 'None'
        ? null
        : criteriaReturnFrequencyIncludeExclude,
    returnFrequencyDomains:
      !criteriaReturnFrequencyIncludeExclude || criteriaReturnFrequencyIncludeExclude === 'None'
        ? null
        : values.criteriaReturnFrequencyDomains,
    returnFrequencyMin:
      !criteriaReturnFrequencyIncludeExclude || criteriaReturnFrequencyIncludeExclude === 'None'
        ? null
        : values.criteriaReturnFrequencyMin,
    returnFrequencyMax:
      !criteriaReturnFrequencyIncludeExclude || criteriaReturnFrequencyIncludeExclude === 'None'
        ? null
        : values.criteriaReturnFrequencyMax,
    statesIncludeExclude:
      !values.criteriaIncludeOrExcludeStates || values.criteriaIncludeOrExcludeStates === 'None'
        ? null
        : values.criteriaIncludeOrExcludeStates,
    statesValues:
      values.criteriaIncludeOrExcludeStates === null ||
      values.criteriaIncludeOrExcludeStates === 'None' ||
      values.criteriaStates === null
        ? null
        : values.criteriaStates,
    tcpaLastOptInInterval: values.criteriaTcpaLastOptInInterval,
    tcpaOptInRequiredOverLastInterval: values.criteriaTcpaLastOptInInterval,
    tcpaOptInRequired: values.criteriaOfferRequiresOptIn,
    userIdLastDigitIncludeExclude:
      !values.criteriaIncludeOrExcludeUserIdLastDigit || values.criteriaIncludeOrExcludeUserIdLastDigit === 'None'
        ? null
        : values.criteriaIncludeOrExcludeUserIdLastDigit,
    userIdLastDigitStringValues:
      !values.criteriaIncludeOrExcludeUserIdLastDigit ||
      values.criteriaIncludeOrExcludeUserIdLastDigit === 'None' ||
      values.criteriaUserIdLastDigitString === null
        ? []
        : values.criteriaUserIdLastDigitString,
    userAttributesIncludeExclude: criteriaIncludeOrExcludeUserAttributes,
    userAttributeRules: criteriaIncludeOrExcludeUserAttributes === null ? null : values.userAttributeRules,
    urlParametersIncludeExclude: criteriaIncludeOrExcludeUrlParameters,
    urlParameterRules: criteriaIncludeOrExcludeUrlParameters === null ? null : values.urlParameterRules,
    userAgentIncludeExclude: criteriaIncludeOrExcludeUserAgent,
    userAgentRules: criteriaIncludeOrExcludeUserAgent === null ? null : values.userAgentRules,
    zipsIncludeExclude:
      !values.criteriaIncludeOrExcludeZips || values.criteriaIncludeOrExcludeZips === 'None'
        ? null
        : values.criteriaIncludeOrExcludeZips,
    zipsValues,
    affSubIDConfiguration: (() => {
      const config = values.affSubIDConfiguration;

      // If no config, return null
      if (!config) {
        return null;
      }

      // Check if andOrLogic is empty - if so, return null
      const hasEmptyAndOrLogic =
        !config.andOrLogic ||
        config.andOrLogic === null ||
        config.andOrLogic === '' ||
        config.andOrLogic.toLowerCase() === 'select';

      if (hasEmptyAndOrLogic) {
        return null;
      }

      // Transform subIdList - convert strings to arrays
      const transformedSubIdList = (config.subIdList || [])
        .map((entry) => {
          const rawSubIds = entry.subIds;
          // Convert string (from textarea) back to array for backend
          const entrySubIds =
            typeof rawSubIds === 'string'
              ? rawSubIds
                  .split('\n')
                  .map((id) => id.trim())
                  .filter((id) => id !== '')
              : Array.isArray(rawSubIds)
              ? rawSubIds.filter((id) => id && String(id).trim() !== '')
              : [];
          return {
            ...entry,
            subIds: entrySubIds,
            includeExclude: entry.includeExclude ?? null,
            affiliate: entry.affiliate ?? null,
          };
        })
        .filter((entry) => {
          const isNullAffiliate =
            typeof entry.affiliate === 'string' && entry.affiliate.trim().toLowerCase() === '[null]';
          const hasIncludeExclude =
            entry.includeExclude && entry.includeExclude.trim() !== '' && entry.includeExclude.toLowerCase() !== 'none';
          const hasAffiliate = entry.affiliate && entry.affiliate.trim() !== '';
          const hasSubIds = entry.subIds && Array.isArray(entry.subIds) && entry.subIds.length > 0;
          return hasIncludeExclude && hasAffiliate && (hasSubIds || isNullAffiliate);
        });

      // If subIdList is empty after filtering, treat as if "Select Logic" was selected (return null)
      if (transformedSubIdList.length === 0) {
        return null;
      }

      // Return transformed configuration
      return {
        ...config,
        andOrLogic: config.andOrLogic.toUpperCase(),
        subIdList: transformedSubIdList,
      };
    })(),
    // Use qualityScore for any non-offer contexts; offers use qualityScoreCriteria
    [context === 'offer' ? 'qualityScoreCriteria' : 'qualityScore']: values.qualityScoreCriteria
      ? {
          qualityScoreRange: (() => {
            // Prefer FieldArray values
            if (
              values.qualityScoreCriteria.qualityScoreRange &&
              Array.isArray(values.qualityScoreCriteria.qualityScoreRange)
            ) {
              const built = values.qualityScoreCriteria.qualityScoreRange.reduce((acc, item, index) => {
                const dayFilters = Array.isArray(item.dayFilters)
                  ? item.dayFilters.filter((v) => typeof v === 'string' && v.length > 0)
                  : item.dayFilter
                  ? [item.dayFilter]
                  : [];
                if (
                  item &&
                  (item.minScore !== null ||
                    item.maxScore !== null ||
                    item.cap !== null ||
                    item.failureProbability !== null ||
                    item.validMultipliers !== null ||
                    dayFilters.length > 0)
                ) {
                  const nameMatch = item.name?.match(/index_(\d+)/);
                  const keyIndex = nameMatch ? nameMatch[1] : index.toString();
                  acc[keyIndex] = {
                    minScore: item.minScore,
                    maxScore: item.maxScore,
                    failureProbability: item.failureProbability,
                    cap: item.cap,
                    validMultipliers: item.validMultipliers,
                    dayFilters,
                  };
                }
                return acc;
              }, {});
              // If nothing built, fall back to legacy min/max
              if (Object.keys(built).length === 0) {
                const legacyMin =
                  values.qualityScoreCriteria && values.qualityScoreCriteria.minScore !== undefined
                    ? values.qualityScoreCriteria.minScore
                    : values.minScore ?? null;
                const legacyMax =
                  values.qualityScoreCriteria && values.qualityScoreCriteria.maxScore !== undefined
                    ? values.qualityScoreCriteria.maxScore
                    : values.maxScore ?? null;
                if (legacyMin !== null || legacyMax !== null) {
                  return {
                    0: {
                      minScore: legacyMin,
                      maxScore: legacyMax,
                      cap: null,
                      failureProbability: 0,
                      validMultipliers: 0,
                      dayFilters: [],
                    },
                  };
                }
              }
              return built;
            }
            // If FieldArray missing, attempt to use legacy min/max
            const legacyMin =
              values.qualityScoreCriteria && values.qualityScoreCriteria.minScore !== undefined
                ? values.qualityScoreCriteria.minScore
                : values.minScore ?? null;
            const legacyMax =
              values.qualityScoreCriteria && values.qualityScoreCriteria.maxScore !== undefined
                ? values.qualityScoreCriteria.maxScore
                : values.maxScore ?? null;
            if (legacyMin !== null || legacyMax !== null) {
              return {
                0: {
                  minScore: legacyMin,
                  maxScore: legacyMax,
                  cap: null,
                  failureProbability: 0,
                  validMultipliers: 0,
                  dayFilters: [],
                },
              };
            }
            return values.qualityScoreCriteria.qualityScoreRange || {};
          })(),
          bypass:
            values.qualityScoreCriteria.bypass && Array.isArray(values.qualityScoreCriteria.bypass)
              ? values.qualityScoreCriteria.bypass.reduce((acc, item, index) => {
                  if (
                    item &&
                    (item.bypass_affiliate?.length > 0 || item.cap !== null || item.failureProbability !== null)
                  ) {
                    // Extract index number from name field (e.g., "index_3" -> "3")
                    const nameMatch = item.name?.match(/index_(\d+)/);
                    const keyIndex = nameMatch ? nameMatch[1] : index.toString();

                    // Convert affiliate objects to numeric IDs
                    const affiliateIds =
                      item.bypass_affiliate
                        ?.map((affiliate) => {
                          if (typeof affiliate === 'object' && affiliate.id) {
                            return affiliate.id;
                          }
                          return affiliate;
                        })
                        .filter((id) => id !== null && id !== undefined) || [];

                    acc[keyIndex] = {
                      bypass_affiliate: affiliateIds,
                      failureProbability: item.failureProbability,
                      cap: item.cap,
                    };
                  }
                  return acc;
                }, {})
              : values.qualityScoreCriteria.bypass || {},
          cap_reset: {
            resetInterval: 'daily',
            resetDayOfWeek: '',
            resetDayOfMonth: '',
            resetDate: '',
          },
        }
      : null,
    randomFailureProbabilities: values.randomFailureProbabilities,
    jobKeyword: cleanedJobKeyword,
  };
  console.log('criteriaObj', criteriaObj);
  return criteriaObj;
}

function parseAdditionalOffers(additionalOffers) {
  const additionalOfferIds = additionalOffers.map((offer) =>
    typeof offer === 'object' && offer !== null ? parseInt(offer.id) : offer
  );
  console.log(additionalOfferIds);
  return additionalOfferIds;
}

function findIdFromName(jsonContainer, name) {
  for (let o of jsonContainer) {
    if (o.name === name) {
      const { id, name, code } = o;
      return { id, name, code };
    }
  }
  return null;
}

function validateQueryRules(query) {
  if (!query || !query.rules) return false;

  return query.rules.every((rule) => {
    // Skip validation for combinators (strings)
    if (typeof rule === 'string') return true;

    if (rule.rules) {
      // This is a group
      return validateQueryRules(rule);
    } else {
      // This is a rule
      return rule.field !== 'Select...' && rule.value !== 'Select...' && rule.field !== '' && rule.value !== null;
    }
  });
}

// Like validateQueryRules but allows null/empty value for "is empty" / "is not empty" operators
function validateUrlParamRules(query) {
  if (!query || !query.rules) return false;

  return query.rules.every((rule) => {
    if (typeof rule === 'string') return true;

    if (rule.rules) {
      return validateUrlParamRules(rule);
    }

    const valueOptional = rule.operator === 'is empty' || rule.operator === 'is not empty';
    return rule.field !== '' && (valueOptional || (rule.value !== null && rule.value !== ''));
  });
}

// User Agent rules: the field is fixed, so only the typed text has to be present
function validateUserAgentRules(query) {
  if (!query || !query.rules) return false;

  return query.rules.every((rule) => {
    if (typeof rule === 'string') return true;

    if (rule.rules) {
      return validateUserAgentRules(rule);
    }

    return rule.value !== null && rule.value !== undefined && String(rule.value).trim() !== '';
  });
}

export function validateCriteria(values) {
  console.log('validateCriteria called with values:', values);
  const errors = {};
  if (!values) return;

  // The criteria fields in the publisherPrePingDetails have a different naming convention than the criteria fields in the other entities.
  // TODO: WET-1290 We'll need to refactor those fields to match the other entities upon save/init
  // Publisher Pre-Ping Details
  if (values.minAge && values.maxAge) {
    if (values.minAge > values.maxAge) {
      errors.minAge = 'Minimum age must be lower than max age.';
    }
  }
  if (values.minAge || values.maxAge) {
    if (values.minAge && values.minAge < 10) {
      errors.minAge = 'Age must be greater than 10.';
    }
    if (values.maxAge && values.maxAge < 10) {
      errors.maxAge = 'Age must be greater than 10.';
    }
  }

  if (values.userIdLastDigitIncludeExclude && values.userIdLastDigitIncludeExclude !== 'None') {
    if (
      !values.userIdLastDigitStringValues ||
      (values.userIdLastDigitStringValues &&
        values.userIdLastDigitStringValues.length === 0 &&
        values.userIdLastDigitIncludeExclude !== 'None')
    ) {
      errors.userIdLastDigitStringValues = 'Please enter at least one digit.';
    }
  }

  // Previous Registration Required
  if (values.returnFrequencyIncludeExclude && values.returnFrequencyIncludeExclude !== 'None') {
    if (
      !values.returnFrequencyDomains ||
      (values.returnFrequencyDomains && values.returnFrequencyDomains.length === 0)
    ) {
      errors.returnFrequencyDomains = 'Please add one or more domains.';
    }
    if (
      values.returnFrequencyDomains &&
      values.returnFrequencyDomains.includes('All Domains') &&
      values.returnFrequencyDomains.length > 1
    ) {
      errors.returnFrequencyDomains = 'Please remove other domains while "All Domains" is selected';
    }
  }

  if (values.returnFrequencyMin && values.returnFrequencyMax) {
    if (parseInt(values.returnFrequencyMin) > parseInt(values.returnFrequencyMax)) {
      errors.returnFrequencyMin = 'Minimum registration must be lower than max registration.';
    }
  }

  // states
  if (values.statesIncludeExclude && values.statesIncludeExclude !== 'None') {
    if (!values.statesValues || (values.statesValues && values.statesValues.length === 0)) {
      errors.statesValues = 'Please enter at least one state.';
    }
  }

  // zips
  if (values.zipsIncludeExclude && values.zipsIncludeExclude !== 'None') {
    const { zipsValues } = values;
    if (!zipsValues || (zipsValues && zipsValues !== null && zipsValues.length === 0)) {
      errors.zipsValues = 'Please add one or more zip codes.';
    } else if (zipsValues) {
      let zipsArray =
        zipsValues && typeof zipsValues !== 'string'
          ? zipsValues
          : typeof zipsValues === 'string'
          ? zipsValues.split(/[\s,]+/)
          : [];
      zipsArray = zipsArray.filter((zip) => zip !== '');
      const invalidZips = zipsArray.filter((zip) => !/^\d{5}$/.test(zip));
      if (invalidZips.length > 0) {
        errors.zipsValues = `Invalid zip code(s): ${invalidZips.join(', ')}`;
      }
    }
  }

  // Email Domains
  if (values.emailDomainsIncludeExclude && values.emailDomainsIncludeExclude !== 'None') {
    if (
      !values.emailDomains ||
      (values.emailDomains && values.emailDomains !== null && values.emailDomains.length === 0)
    ) {
      errors.emailDomains = 'Please add one or more email domains.';
    }
  }

  // Pre-ping

  // Internal
  if (values.prePingSuppressionType) {
    if (!values.prePingSuppressionAmount || values.prePingSuppressionAmount === null) {
      errors.prePingSuppressionAmount = 'Please select a suppression amount.';
    }

    if (values.suppressionRules && values.suppression_type !== 'answer')
      values.suppressionRules.suppression_answer = null;

    if (!values.prePingSuppressionLevel || values.prePingSuppressionLevel === 'None') {
      errors.prePingSuppressionLevel = 'Please select a suppression level';
    }
  }

  if (values.suppressionRules && values.suppressionRules.suppression_type !== 'answer')
    values.suppressionRules.suppression_answer = null;

  // Criteria
  // USERS
  // Age requirements
  if (values.criteriaMinAge && values.criteriaMaxAge) {
    if (values.criteriaMinAge > values.criteriaMaxAge) {
      errors.criteriaMinAge = 'Minimum age must be lower than max age.';
    }
  }
  if (values.criteriaMinAge || values.criteriaMaxAge) {
    if (values.criteriaMinAge && values.criteriaMinAge < 10) {
      errors.criteriaMinAge = 'Age must be greater than 10.';
    }
    if (values.criteriaMaxAge && values.criteriaMaxAge < 10) {
      errors.criteriaMaxAge = 'Age must be greater than 10.';
    }
  }

  // User ID digits
  if (values.criteriaIncludeOrExcludeUserIdLastDigit && values.criteriaIncludeOrExcludeUserIdLastDigit !== 'None') {
    if (
      !values.criteriaUserIdLastDigitString ||
      (values.criteriaUserIdLastDigitString &&
        values.criteriaUserIdLastDigitString !== null &&
        values.criteriaUserIdLastDigitString.length === 0)
    ) {
      errors.criteriaUserIdLastDigitString = 'Please add one or more user ID digits.';
    }
  }

  // Previous Registrations
  if (
    values.previousRegistrationConfiguration?.andOrLogic &&
    values.previousRegistrationConfiguration?.andOrLogic !== null &&
    values.previousRegistrationConfiguration?.andOrLogic.toLowerCase() !== 'select'
  ) {
    errors.previousRegistrationConfiguration = {};
    const previousRegistrationErrors = [];
    if (!Array.isArray(values.previousRegistrationConfiguration?.previousRegistrations)) {
      values.previousRegistrationConfiguration.previousRegistrations = [{}]; // Add a default object
    }
    if (Array.isArray(values.previousRegistrationConfiguration?.previousRegistrations)) {
      values.previousRegistrationConfiguration.previousRegistrations.forEach((previousReg) => {
        const previousRegistrationError = {};

        if (!previousReg?.includeExcludeAction || previousReg?.includeExcludeAction?.toLowerCase() === 'none') {
          previousRegistrationError.includeExcludeAction = 'Please select include or exclude.';
        } else if (
          previousReg?.includeExcludeAction?.toLowerCase() === 'include' ||
          previousReg?.includeExcludeAction?.toLowerCase() === 'exclude'
        ) {
          if (previousReg.domains.length === 0) {
            previousRegistrationError.domains = 'Please add one or more domains.';
          } else if (
            previousReg.domains &&
            previousReg.domains.includes('All Domains') &&
            previousReg.domains.length > 1
          ) {
            previousRegistrationError.domains = 'Please remove other domains while "All Domains" is selected';
          }
          if (!previousReg.previousRegistrationsMin && !previousReg.previousRegistrationsMax) {
            previousRegistrationError.previousRegistrationsMin = 'Please add a Min or a Max.';
            previousRegistrationError.previousRegistrationsMax = 'Please add a Min or a Max.';
          }
          if (previousReg.previousRegistrationsMin && previousReg.previousRegistrationsMax) {
            if (parseInt(previousReg.previousRegistrationsMin) > parseInt(previousReg.previousRegistrationsMax)) {
              previousRegistrationError.previousRegistrationsMin =
                'Minimum registration must be lower than max registration.';
            }
          }
        }
        previousRegistrationErrors.push(previousRegistrationError);
      });
    } else {
      console.error('previousRegistrations is not an array!');
    }

    errors.previousRegistrationConfiguration.previousRegistrations = previousRegistrationErrors;
  }

  // IPQS

  const isValidIpqsScore = (num) => (typeof num === 'number' ? num >= 0 && num <= 100 : true);
  const ipqsErrorMsg = 'Score must be a number between 0 and 100';
  if (!errors.ipqsConfiguration) errors.ipqsConfiguration = {};

  if (!isValidIpqsScore(values.ipqsConfiguration?.email)) {
    errors.ipqsConfiguration.email = ipqsErrorMsg;
  }
  if (!isValidIpqsScore(values.ipqsConfiguration?.phone)) {
    errors.ipqsConfiguration.phone = ipqsErrorMsg;
  }
  if (!isValidIpqsScore(values.ipqsConfiguration?.ip)) {
    errors.ipqsConfiguration.ip = ipqsErrorMsg;
  }
  if (!isValidIpqsScore(values.ipqsConfiguration?.fingerprint)) {
    errors.ipqsConfiguration.fingerprint = ipqsErrorMsg;
  }

  // Devices
  if (values.criteriaIncludeOrExcludeDevices && values.criteriaIncludeOrExcludeDevices !== 'None') {
    if (
      !values.criteriaDevices ||
      (values.criteriaDevices && values.criteriaDevices !== null && values.criteriaDevices.length === 0)
    ) {
      errors.criteriaDevices = 'Please add one or more devices.';
    }
  }

  // AIB List

  if (values.criteriaAibListIncludeExclude && values.criteriaAibListIncludeExclude !== 'None') {
    if (!values.criteriaAibList || (values.criteriaAibList && values.criteriaAibList.length === 0)) {
      errors.criteriaAibList = 'Please add one or more AIB list codes.';
    }
  }

  // Browsers

  if (values.criteriaIncludeOrExcludeBrowsers && values.criteriaIncludeOrExcludeBrowsers !== 'None') {
    if (
      !values.criteriaBrowsers ||
      (values.criteriaBrowsers && values.criteriaBrowsers !== null && values.criteriaBrowsers.length === 0)
    ) {
      errors.criteriaBrowsers = 'Please add one or more browsers.';
    }
  }

  // What If Domains

  if (values.criteriaIncludeOrExcludeDomains && values.criteriaIncludeOrExcludeDomains !== 'None') {
    if (
      !values.criteriaDomains ||
      (values.criteriaDomains && values.criteriaDomains !== null && values.criteriaDomains.length === 0)
    ) {
      errors.criteriaDomains = 'Please add one or more domains.';
    }
  }

  // Domain - Affiliates

  if (values.domainAffiliateConfiguration != null) {
    errors.domainAffiliateConfiguration = {};
    const domainAffiliateErrors = [];
    values.domainAffiliateConfiguration.domainAffiliateEntries.forEach((domainAffiliate, i) => {
      const domainAffiliateError = {};
      if (
        (domainAffiliate.affiliates.length !== 0 || domainAffiliate.domains.length !== 0) &&
        (!domainAffiliate?.includeExcludeAction || domainAffiliate?.includeExcludeAction?.toLowerCase() === 'none')
      ) {
        domainAffiliateError.includeExcludeAction = 'Please select include or exclude.';
      } else if (
        domainAffiliate?.includeExcludeAction?.toLowerCase() === 'include' ||
        domainAffiliate?.includeExcludeAction?.toLowerCase() === 'exclude'
      ) {
        if (domainAffiliate.affiliates.length === 0)
          domainAffiliateError.affiliates = 'Please add one or more affiliates.';
        if (domainAffiliate.domains.length === 0) domainAffiliateError.domains = 'Please add one or more domains.';
      }
      domainAffiliateErrors.push(domainAffiliateError);
    });
    errors.domainAffiliateConfiguration.domainAffiliateEntries = domainAffiliateErrors;
  }

  // Operating systems
  if (values.criteriaIncludeOrExcludeOperatingSystems && values.criteriaIncludeOrExcludeOperatingSystems !== 'None') {
    if (
      !values.criteriaOperatingSystems ||
      (values.criteriaOperatingSystems &&
        values.criteriaOperatingSystems !== null &&
        values.criteriaOperatingSystems.length === 0)
    ) {
      errors.criteriaOperatingSystems = 'Please add one or more operating systems.';
    }
  }

  // States
  if (values.criteriaIncludeOrExcludeStates && values.criteriaIncludeOrExcludeStates !== 'None') {
    if (
      !values.criteriaStates ||
      (values.criteriaStates && values.criteriaStates !== null && values.criteriaStates.length === 0)
    ) {
      errors.criteriaStates = 'Please add one or more states.';
    }
  }

  // Zips
  if (values.criteriaIncludeOrExcludeZips && values.criteriaIncludeOrExcludeZips !== 'None') {
    const { criteriaZips } = values;
    if (!criteriaZips || (criteriaZips && criteriaZips !== null && criteriaZips.length === 0)) {
      errors.criteriaZips = 'Please add one or more zip codes.';
    } else if (criteriaZips) {
      let zipsArray =
        criteriaZips && typeof criteriaZips !== 'string'
          ? criteriaZips
          : typeof criteriaZips === 'string'
          ? criteriaZips.split(/[\s,]+/)
          : [];
      zipsArray = zipsArray.filter((zip) => zip !== '');
      const invalidZips = zipsArray.filter((zip) => !/^\d{5}$/.test(zip));
      if (invalidZips.length > 0) {
        errors.criteriaZips = `Invalid zip code(s): ${invalidZips.join(', ')}`;
      }
    }
  }
  // ATTRIBUTE

  // Criteria Questions
  if (values.criteriaQuestion) {
    if (
      !values.criteriaQuestionAnswers ||
      (values.criteriaQuestionAnswers && values.criteriaQuestionAnswers.length === 0)
    ) {
      errors.criteriaQuestionAnswers = 'Please add one or more question answers.';
    }
  }

  // User Attributes
  if (values.criteriaIncludeOrExcludeUserAttributes && values.criteriaIncludeOrExcludeUserAttributes !== 'None') {
    if (!values.userAttributeRules || values.userAttributeRules?.rules?.length === 0) {
      errors.criteriaIncludeOrExcludeUserAttributes = 'Add a rule';
    } else if (!validateQueryRules(values.userAttributeRules)) {
      errors.criteriaIncludeOrExcludeUserAttributes = 'Please complete all rule fields';
    }
  }

  // URL Parameters
  if (values.criteriaIncludeOrExcludeUrlParameters && values.criteriaIncludeOrExcludeUrlParameters !== 'None') {
    if (!values.urlParameterRules || values.urlParameterRules?.rules?.length === 0) {
      errors.criteriaIncludeOrExcludeUrlParameters = 'Add a rule';
    } else if (!validateUrlParamRules(values.urlParameterRules)) {
      errors.criteriaIncludeOrExcludeUrlParameters = 'Please complete all rule fields';
    }
  }

  // User Agent
  if (values.criteriaIncludeOrExcludeUserAgent && values.criteriaIncludeOrExcludeUserAgent !== 'None') {
    if (!values.userAgentRules || values.userAgentRules?.rules?.length === 0) {
      errors.criteriaIncludeOrExcludeUserAgent = 'Add a rule';
    } else if (!validateUserAgentRules(values.userAgentRules)) {
      errors.criteriaIncludeOrExcludeUserAgent = 'Enter the text the user agent should contain';
    }
  }

  // Affiliate Names
  if (values.criteriaIncludeOrExcludeAffNames && values.criteriaIncludeOrExcludeAffNames !== 'None') {
    if (
      !values.criteriaAffNames ||
      (values.criteriaAffNames && values.criteriaAffNames !== null && values.criteriaAffNames.length === 0)
    ) {
      errors.criteriaAffNames = 'Please add one or more affiliate names.';
    }
  }

  // Quality Score
  if (values.qualityScoreCriteria) {
    console.log('Validating qualityScoreCriteria:', values.qualityScoreCriteria);
    console.log('qualityScoreRange type:', typeof values.qualityScoreCriteria.qualityScoreRange);
    console.log('qualityScoreRange isArray:', Array.isArray(values.qualityScoreCriteria.qualityScoreRange));
    console.log('qualityScoreRange value:', values.qualityScoreCriteria.qualityScoreRange);
    const { qualityScoreRange } = values.qualityScoreCriteria;

    // Validate individual ranges
    if (qualityScoreRange && Array.isArray(qualityScoreRange)) {
      // Initialize errors structure for FieldArray
      if (!errors.qualityScoreCriteria) errors.qualityScoreCriteria = {};
      if (!errors.qualityScoreCriteria.qualityScoreRange) errors.qualityScoreCriteria.qualityScoreRange = [];

      qualityScoreRange.forEach((range, index) => {
        if (range) {
          const { minScore, maxScore, failureProbability, validMultipliers } = range;

          // Initialize error object for this range
          errors.qualityScoreCriteria.qualityScoreRange[index] = {};

          // Validate individual range
          if (minScore && maxScore && parseInt(minScore) > parseInt(maxScore)) {
            errors.qualityScoreCriteria.qualityScoreRange[index].minScore =
              'Minimum score must be lower than maximum score';
          }
          if (minScore && parseInt(minScore) < 0) {
            errors.qualityScoreCriteria.qualityScoreRange[index].minScore = 'Score must be above 0';
          }
          if (maxScore && parseInt(maxScore) < 0) {
            errors.qualityScoreCriteria.qualityScoreRange[index].maxScore = 'Score must be above 0';
          }

          // Validate failure probability
          if (failureProbability !== null && failureProbability !== undefined && failureProbability !== '') {
            const prob = parseFloat(failureProbability);
            if (isNaN(prob) || prob < 0 || prob >= 1) {
              errors.qualityScoreCriteria.qualityScoreRange[index].failureProbability =
                'Failure probability must be between 0.00 and 0.99';
            }
          }

          // Validate valid multipliers
          if (validMultipliers !== null && validMultipliers !== undefined && validMultipliers !== '') {
            const mult = parseInt(validMultipliers);
            if (isNaN(mult) || mult < 0) {
              errors.qualityScoreCriteria.qualityScoreRange[index].validMultipliers =
                'Minimum Valid Multipliers must be 0 or greater';
            }
          }
        }
      });

      // Validate overlapping ranges (respecting day filters)
      const allDays = ['monday', 'tuesday', 'wednesday', 'thursday', 'friday', 'saturday', 'sunday'];
      const expandDaysForRange = (range) => {
        const raw = Array.isArray(range?.dayFilters) ? range.dayFilters : [];
        // Empty or falsy => All Days
        if (!raw || raw.length === 0) {
          return new Set(allDays);
        }

        const days = new Set();
        raw.forEach((token) => {
          const v = typeof token === 'string' ? token.toLowerCase() : '';
          if (!v) return;
          if (v === 'weekdays') {
            ['monday', 'tuesday', 'wednesday', 'thursday', 'friday'].forEach((d) => days.add(d));
          } else if (v === 'weekend') {
            ['saturday', 'sunday'].forEach((d) => days.add(d));
          } else if (allDays.includes(v)) {
            days.add(v);
          }
        });

        // If nothing resolved, treat as All Days to be safe
        return days.size > 0 ? days : new Set(allDays);
      };

      const validRanges = qualityScoreRange.filter((range) => {
        if (!range) return false;
        const hasMin = range.minScore !== null && range.minScore !== undefined && range.minScore !== '';
        const hasMax = range.maxScore !== null && range.maxScore !== undefined && range.maxScore !== '';
        // Only consider ranges that have at least one bound set
        return hasMin || hasMax;
      });

      for (let i = 0; i < validRanges.length; i++) {
        for (let j = i + 1; j < validRanges.length; j++) {
          const range1 = validRanges[i];
          const range2 = validRanges[j];

          // If the ranges never apply on the same day, they are allowed even if scores overlap
          const days1 = expandDaysForRange(range1);
          const days2 = expandDaysForRange(range2);
          const hasCommonDay = [...days1].some((d) => days2.has(d));
          if (!hasCommonDay) continue;

          const hasMin1 = range1.minScore !== null && range1.minScore !== undefined && range1.minScore !== '';
          const hasMax1 = range1.maxScore !== null && range1.maxScore !== undefined && range1.maxScore !== '';
          const hasMin2 = range2.minScore !== null && range2.minScore !== undefined && range2.minScore !== '';
          const hasMax2 = range2.maxScore !== null && range2.maxScore !== undefined && range2.maxScore !== '';

          // For overlap purposes:
          // - missing min behaves like 0 (no minimum)
          // - missing max behaves like +infinity (no maximum)
          const min1 = hasMin1 ? parseInt(range1.minScore) : 0;
          const max1 = hasMax1 ? parseInt(range1.maxScore) : Number.POSITIVE_INFINITY;
          const min2 = hasMin2 ? parseInt(range2.minScore) : 0;
          const max2 = hasMax2 ? parseInt(range2.maxScore) : Number.POSITIVE_INFINITY;

          // Check for overlap on any shared day: ranges overlap if one starts before the other ends
          if (min1 <= max2 && max1 >= min2) {
            console.log(`Found overlap (with shared days): ${min1}-${max1} overlaps with ${min2}-${max2}`);
            // Find the original indices in the full array
            const originalIndex1 = qualityScoreRange.findIndex((range) => range === range1);
            const originalIndex2 = qualityScoreRange.findIndex((range) => range === range2);

            if (!errors.qualityScoreCriteria.qualityScoreRange[originalIndex1]) {
              errors.qualityScoreCriteria.qualityScoreRange[originalIndex1] = {};
            }
            if (!errors.qualityScoreCriteria.qualityScoreRange[originalIndex2]) {
              errors.qualityScoreCriteria.qualityScoreRange[originalIndex2] = {};
            }

            if (!errors.qualityScoreCriteria.qualityScoreRange[originalIndex1].minScore) {
              errors.qualityScoreCriteria.qualityScoreRange[originalIndex1].minScore = {
                message: `Score range ${min1}-${max1} overlaps with another range on at least one shared day. Ranges must be mutually exclusive for any given day.`,
              };
            }
            if (!errors.qualityScoreCriteria.qualityScoreRange[originalIndex2].minScore) {
              // Mark second range as invalid for UI (exclamation), but omit message so text doesn't stack
              errors.qualityScoreCriteria.qualityScoreRange[originalIndex2].minScore = { _overlap: true };
            }
          }
        }
      }
    }

    // Validate bypass section
    const { bypass } = values.qualityScoreCriteria;
    if (bypass && Array.isArray(bypass)) {
      console.log('Validating bypass section:', bypass);

      // Initialize errors structure for FieldArray
      if (!errors.qualityScoreCriteria) errors.qualityScoreCriteria = {};
      if (!errors.qualityScoreCriteria.bypass) errors.qualityScoreCriteria.bypass = [];

      // First pass: validate individual fields
      bypass.forEach((bypassItem, index) => {
        if (bypassItem) {
          const { bypass_affiliate, cap, failureProbability } = bypassItem;

          // Initialize error object for this bypass item
          errors.qualityScoreCriteria.bypass[index] = {};

          // Validate that at least one affiliate is selected
          if (!bypass_affiliate || bypass_affiliate.length === 0) {
            errors.qualityScoreCriteria.bypass[index].bypass_affiliate =
              'Please select at least one affiliate for bypass.';
          }

          // Validate cap amount
          if (cap !== null && cap !== undefined && cap !== '' && parseInt(cap) < 0) {
            errors.qualityScoreCriteria.bypass[index].cap = 'Cap amount must be 0 or greater.';
          }

          // Validate failure probability
          if (failureProbability !== null && failureProbability !== undefined && failureProbability !== '') {
            const prob = parseFloat(failureProbability);
            if (isNaN(prob) || prob < 0 || prob >= 1) {
              errors.qualityScoreCriteria.bypass[index].failureProbability =
                'Failure probability must be between 0.00 and 0.99';
            }
          }
        }
      });

      // Second pass: check for duplicate affiliates across all bypass rows
      const allAffiliates = [];
      const affiliateToRowMap = new Map(); // Map affiliate ID to row index

      bypass.forEach((bypassItem, index) => {
        if (bypassItem && bypassItem.bypass_affiliate && Array.isArray(bypassItem.bypass_affiliate)) {
          bypassItem.bypass_affiliate.forEach((affiliate) => {
            // Handle both object format {id: 1006, name: "Affiliate Name"} and direct ID format
            const affiliateId = typeof affiliate === 'object' ? affiliate.id : affiliate;
            if (affiliateId !== null && affiliateId !== undefined) {
              if (affiliateToRowMap.has(affiliateId)) {
                // This affiliate is already used in another row
                const existingRowIndex = affiliateToRowMap.get(affiliateId);
                console.log(`Duplicate affiliate ${affiliateId} found in rows ${existingRowIndex} and ${index}`);

                // Add error to both rows
                if (!errors.qualityScoreCriteria.bypass[existingRowIndex]) {
                  errors.qualityScoreCriteria.bypass[existingRowIndex] = {};
                }
                if (!errors.qualityScoreCriteria.bypass[index]) {
                  errors.qualityScoreCriteria.bypass[index] = {};
                }

                errors.qualityScoreCriteria.bypass[
                  existingRowIndex
                ].bypass_affiliate = `Affiliate ${affiliateId} is already used in another bypass row.`;
                errors.qualityScoreCriteria.bypass[
                  index
                ].bypass_affiliate = `Affiliate ${affiliateId} is already used in another bypass row.`;
              } else {
                affiliateToRowMap.set(affiliateId, index);
              }
            }
          });
        }
      });
    }

    // Legacy validation for old single range format
    const { minScore, maxScore } = values.qualityScoreCriteria;
    if (minScore && maxScore && parseInt(minScore) > parseInt(maxScore)) {
      if (!errors.qualityScoreCriteria) errors.qualityScoreCriteria = {};
      errors.qualityScoreCriteria.minScore = 'Minimum score must be lower than maximum score';
    }
    if (minScore && parseInt(minScore) < 0) {
      if (!errors.qualityScoreCriteria) errors.qualityScoreCriteria = {};
      errors.qualityScoreCriteria.minScore = 'Score must be above 0';
    }
    if (maxScore && parseInt(maxScore) < 0) {
      if (!errors.qualityScoreCriteria) errors.qualityScoreCriteria = {};
      errors.qualityScoreCriteria.maxScore = 'Score must be above 0';
    }
  }

  // Email Domains
  if (values.criteriaIncludeOrExcludeEmailDomains && values.criteriaIncludeOrExcludeEmailDomains !== 'None') {
    if (
      !values.criteriaEmailDomains ||
      (values.criteriaEmailDomains && values.criteriaEmailDomains !== null && values.criteriaEmailDomains.length === 0)
    ) {
      errors.criteriaEmailDomains = 'Please add one or more email domains.';
    }
  }

  // Validate Landing Page Group and Landing Page IDs
  if (values.criteriaLandingPageGroupIncludeExclude && values.criteriaLandingPageGroupIncludeExclude !== 'None') {
    if (!values.criteriaLandingPageGroupIds || values.criteriaLandingPageGroupIds.length === 0) {
      errors.criteriaLandingPageGroupIds = 'Please add one or more landing page groups.';
    }

    if (values.criteriaLandingPageGroupIds && values.criteriaLandingPageGroupIds.length == 0) {
      errors.criteriaLandingPageGroupIds = 'Please select at least one landing page group.';
    }
  }

  if (values.criteriaLandingPageIncludeExclude && values.criteriaLandingPageIncludeExclude !== 'None') {
    if (!values.criteriaLandingPageIds || values.criteriaLandingPageIds.length === 0) {
      errors.criteriaLandingPageIds = 'Please add one or more landing pages.';
    }

    if (values.criteriaLandingPageIds && values.criteriaLandingPageIds.length === 0) {
      errors.criteriaLandingPageIds = 'Please select at least one landing page.';
    }
  }

  if (values.landingPageGroupIncludeExclude && values.landingPageGroupIncludeExclude !== 'None') {
    if (!values.landingPageGroupIds || values.landingPageGroupIds.length === 0) {
      errors.landingPageGroupIds = 'Please add one or more landing page groups.';
    }

    if (values.landingPageGroupIds && values.landingPageGroupIds.length == 0) {
      errors.landingPageGroupIds = 'Please select at least one landing page group.';
    }
  }

  if (values.landingPageIncludeExclude && values.landingPageIncludeExclude !== 'None') {
    if (!values.landingPageIds || values.landingPageIds.length === 0) {
      errors.landingPageIds = 'Please add one or more landing pages.';
    }

    if (values.landingPageIds && values.landingPageIds.length === 0) {
      errors.landingPageIds = 'Please select at least one landing page.';
    }
  }

  // Pre-ping

  // Internal
  let { suppressionRules } = values;
  suppressionRules = typeof suppressionRules === 'object' && suppressionRules !== null ? suppressionRules : {};
  const duration =
    typeof suppressionRules.duration === 'object' && suppressionRules.duration !== null
      ? suppressionRules.duration
      : {};
  const type = duration.type || '';
  const answer = suppressionRules.suppression_answer || '';
  errors.suppressionRules = {};
  if (type && type.toLowerCase() !== 'none') {
    if (!duration.amount || parseInt(duration.amount) === 0) {
      errors.suppressionRules = { ...errors.suppressionRules };
      errors.suppressionRules.duration = {
        amount: 'Please select a suppression amount.',
      };
    }

    if (
      (suppressionRules.suppression_type &&
        (suppressionRules.suppression_type === 'select' ||
          suppressionRules.suppression_type.toLowerCase() === 'none')) ||
      !suppressionRules.suppression_type
    ) {
      errors.suppressionRules = { ...errors.suppressionRules };
      errors.suppressionRules.suppression_type = 'Please select a suppression level.';
    }

    if (suppressionRules.suppression_type === 'answer' && answer === '') {
      errors.suppressionRules = { ...errors.suppressionRules };
      errors.suppressionRules.suppression_answer = 'Please select a suppression answer.';
    }
  }

  let { internalPrePingConfiguration } = values;
  if (internalPrePingConfiguration && internalPrePingConfiguration !== null) {
    errors.internalPrePingConfiguration = {};
    const internalPrePingPingErrors = [];
    const validSuppressionTypes = ['days', 'hours', 'minutes'];
    if (Array.isArray(values.internalPrePingConfiguration?.internalPrePings)) {
      values.internalPrePingConfiguration.internalPrePings.forEach((internalPrePing) => {
        const internalPrePingError = {};

        // Only validate entries with valid suppression types
        // Entries with invalid types (none, select, empty) will be filtered out on save
        const hasValidType =
          internalPrePing?.prePingSuppressionType &&
          validSuppressionTypes.includes(internalPrePing.prePingSuppressionType.toLowerCase());

        if (hasValidType) {
          if (!internalPrePing.prePingSuppressionAmount || internalPrePing.prePingSuppressionAmount === 0) {
            internalPrePingError.prePingSuppressionAmount = 'Please select a suppression amount.';
          }
          if (
            !internalPrePing.prePingSuppressionLevel ||
            internalPrePing.prePingSuppressionLevel.toLowerCase() === 'none' ||
            internalPrePing.prePingSuppressionLevel.toLowerCase() === 'select'
          ) {
            internalPrePingError.prePingSuppressionLevel = 'Please select a suppression level.';
          }
          if (internalPrePing.prePingSuppressionLevel === 'answer' && internalPrePing.prePingSuppressionAnswer === '') {
            internalPrePingError.prePingSuppressionAnswer = 'Please select a suppression answer.';
          }
        }
        // No error for entries with invalid types - they will be filtered out on save

        internalPrePingPingErrors.push(internalPrePingError);
      });
    }

    errors.internalPrePingConfiguration.internalPrePings = internalPrePingPingErrors;

    // Analysis Section
    if (values.randomFailureProbabilities && values.randomFailureProbabilities.length > 0) {
      const randomFailureErrors = [];
      values.randomFailureProbabilities.forEach((failureRule, index) => {
        const ruleErrors = {};

        // Validate probability
        if (failureRule.probability === undefined || failureRule.probability === null) {
          ruleErrors.probability = 'Probability is required';
        } else if (failureRule.probability < 0 || failureRule.probability > 1) {
          ruleErrors.probability = 'Probability must be between 0.00 and 1.00';
        }

        // Validate condition if present
        if (failureRule.condition) {
          try {
            JSON.parse(failureRule.condition);
          } catch (e) {
            ruleErrors.condition = 'Invalid condition format';
          }
        }

        if (Object.keys(ruleErrors).length > 0) {
          randomFailureErrors[index] = ruleErrors;
        }
      });

      if (randomFailureErrors.length > 0) {
        errors.randomFailureProbabilities = randomFailureErrors;
      }
    }
  }

  // External

  const { prePingMethod } = values;

  if (prePingMethod && prePingMethod !== null && prePingMethod !== 'NONE') {
    if (!values.prePingHandlerId) {
      errors.prePingHandlerId = 'Please select a handler.';
    }
  }

  // Daily Restriction

  let { criteriaDayHourRestrictions, criteriaDailyLimit, criteriaDailyLimitAnswers, offerType, offerActionType } =
    values;
  if (criteriaDailyLimit && criteriaDailyLimit !== null) {
    if (
      (criteriaDailyLimitAnswers === null || (criteriaDailyLimitAnswers && criteriaDailyLimitAnswers.length === 0)) &&
      offerType?.id !== 9 &&
      offerType?.id !== 10 &&
      !offerActionType
    ) {
      errors.criteriaDailyLimitAnswers = 'Please select one or more answers.';
    }
  }
  criteriaDayHourRestrictions =
    !criteriaDayHourRestrictions || criteriaDayHourRestrictions === null ? [] : criteriaDayHourRestrictions;
  errors.criteriaDayHourRestrictions = [];
  criteriaDayHourRestrictions.forEach((restriction, i) => {
    let start, startMeridiem, end, endMeridiem;
    errors.criteriaDayHourRestrictions[i] = {};
    const currentRestriction = errors.criteriaDayHourRestrictions[i];
    if (restriction.restrictionStart && restriction.restrictionStart != undefined) {
      let splitTime = restriction.restrictionStart.split(' ');
      start = splitTime[0];
      startMeridiem = splitTime[1];
    }
    if (restriction.restrictionEnd && restriction.restrictionStart != undefined) {
      let splitTime = restriction.restrictionEnd.split(' ');
      end = splitTime[0];
      endMeridiem = splitTime[1];
    }

    if (!restriction.restriction) {
      currentRestriction.restriction = 'Please select a restriction type.';
    }

    if (restriction.restriction === 'Partially Restricted') {
      if (!restriction.restrictionStart || restriction.restrictionStart === 'undefined') {
        currentRestriction.restrictionStart = 'Please select a start time.';
      }
      if (!restriction.restrictionEnd || restriction.restrictionEnd === 'undefined') {
        currentRestriction.restrictionEnd = 'Please select an end time.';
      }
    }

    if (start && start !== 'undefined' && end && end !== 'undefined') {
      const startDate = parse(restriction.restrictionStart, 'h:mm aa', new Date());
      const endDate = parse(restriction.restrictionEnd, 'h:mm aa', new Date());
      if (isBefore(endDate, startDate)) {
        handleTimingConflict(errors, i);
      }
    }
  });

  if (
    values?.capAmount &&
    !(values?.capInterval || values?.capInterval === 'None' || values?.capInterval === 'Select')
  ) {
    errors.capInterval = 'Please select a cap interval.';
  }

  // Skip validation if capInterval is 'Select' or 'None'
  if (values.capInterval && values.capInterval !== 'Select' && values.capInterval !== 'None') {
    if (values.capAmount == null || values?.capAmount < 1) {
      console.log(values.capAmount);
      errors.capAmount = 'Please enter a cap amount.';
    }
  }

  // Cap Segments
  let totalSegmentAmount = 0;
  if (
    values.offerType &&
    values.offerType.name !== 'Offer Block' &&
    values.capSegmentConfiguration !== undefined &&
    values.capSegmentConfiguration !== null
  ) {
    errors.capSegmentConfiguration = {};
    errors.capSegmentConfiguration.capSegments = [];
    values?.capSegmentConfiguration?.capSegments.forEach((segment, i) => {
      totalSegmentAmount += segment.segmentAmount;
      errors.capSegmentConfiguration.capSegments[i] = {};
      if (!segment.domains || segment.domains.length === 0) {
        errors.capSegmentConfiguration.capSegments[i].domains = 'Please add one or more domains.';
      }
      if (!segment.segmentAmount || segment.segmentAmount === 0) {
        errors.capSegmentConfiguration.capSegments[i].segmentAmount = 'Please enter a segment amount.';
      }
      if (segment.segmentAmount && (values.capAmount == null || values?.capAmount < 1)) {
        errors.capAmount = 'Please enter a cap amount.';
      }
    });
  }
  if (totalSegmentAmount !== null && values.capAmount !== null && totalSegmentAmount > values.capAmount) {
    errors.capAmount = 'Cap amount must be greater than segment amount.';
  }

  // Aff SubID Configuration
  // Only validate if andOrLogic is explicitly set to AND, OR, or ANY (not empty, null, or 'select')
  // Match previousRegistration validation structure exactly
  if (
    values.affSubIDConfiguration?.andOrLogic &&
    values.affSubIDConfiguration.andOrLogic !== null &&
    values.affSubIDConfiguration.andOrLogic !== '' &&
    values.affSubIDConfiguration.andOrLogic.toLowerCase() !== 'select' &&
    (values.affSubIDConfiguration.andOrLogic.toLowerCase() === 'and' ||
      values.affSubIDConfiguration.andOrLogic.toLowerCase() === 'or' ||
      values.affSubIDConfiguration.andOrLogic.toLowerCase() === 'any')
  ) {
    errors.affSubIDConfiguration = {};
    const affSubIdErrors = [];
    if (!Array.isArray(values.affSubIDConfiguration?.subIdList)) {
      values.affSubIDConfiguration.subIdList = [{}]; // Add a default object
    }
    if (Array.isArray(values.affSubIDConfiguration?.subIdList)) {
      // If array is empty, create an error entry for display
      if (values.affSubIDConfiguration.subIdList.length === 0) {
        const emptyError = {
          includeExclude: 'Please add at least one entry with Include/Exclude.',
          affiliate: 'Please add at least one entry with an Affiliate.',
          subIds: 'Please add at least one entry with Sub IDs.',
        };
        affSubIdErrors.push(emptyError);
      } else {
        // Validate each entry
        values.affSubIDConfiguration.subIdList.forEach((entry) => {
          const affSubIdError = {};

          if (!entry?.includeExclude || entry.includeExclude?.toLowerCase() === 'none' || entry.includeExclude === '') {
            affSubIdError.includeExclude = 'Please select include or exclude.';
          } else if (
            entry.includeExclude?.toLowerCase() === 'include' ||
            entry.includeExclude?.toLowerCase() === 'exclude'
          ) {
            const isNullAffiliate =
              typeof entry.affiliate === 'string' && entry.affiliate.trim().toLowerCase() === '[null]';
            if (!entry.affiliate) {
              affSubIdError.affiliate = 'Please select an affiliate.';
            }
            if (
              !isNullAffiliate &&
              (!entry.subIds ||
                (typeof entry.subIds === 'string' && entry.subIds.trim() === '') ||
                (Array.isArray(entry.subIds) && entry.subIds.length === 0))
            ) {
              affSubIdError.subIds = 'Please enter one or more sub IDs.';
            }
          }
          affSubIdErrors.push(affSubIdError);
        });
      }
    } else {
      console.error('subIdList is not an array!');
    }

    errors.affSubIDConfiguration.subIdList = affSubIdErrors;
  }

  // Job Keyword Offer Actions Criteria
  if (values.jobKeyword) {
    const jk = values.jobKeyword || {};
    const includeExclude = jk.includeExclude;
    const rawKeywords = jk.jobKeywords || '';

    if (['Include', 'Exclude'].includes(includeExclude)) {
      const kwArr = rawKeywords.split(/\s*,\s*/).filter(Boolean);

      if (kwArr.length === 0) {
        errors.jobKeyword = {
          jobKeywords: 'Enter at least one keyword.',
        };
      }
    }
  }
  console.log('validateCriteria returning errors:', errors);
  return errors;
}

const handleTimingConflict = (errors, index) => {
  errors.criteriaDayHourRestrictions[index] = {
    ...errors.criteriaDayHourRestrictions[index],
  };
  errors.criteriaDayHourRestrictions[index].restrictionStart = 'Your start time must be earlier than your end time.';
  errors.criteriaDayHourRestrictions[index].restrictionEnd = 'Your start time must be earlier than your end time.';
};
